"use client";

import {
  AlertCircle,
  CalendarDays,
  Check,
  CircleHelp,
  Minus,
  Plus,
  Ticket,
  UserPlus,
  X,
} from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { toast } from "sonner";
import {
  type BookingContactDetails,
  getBookingContactDetails,
} from "@/actions/booking-contact-details";
import {
  createEventBooking,
  joinEventWaitlist,
  leaveEventWaitlist,
} from "@/actions/event";
import {
  type BookingAttendeeSuggestion,
  validateBookingAttendee,
} from "@/actions/event-booking";
import { getExchangeRate } from "@/actions/forex";
import type { PackForSaleRow } from "@/actions/packs";
import {
  type PackTenderPreview,
  previewEventPackTender,
} from "@/actions/packs";
import { useAuthPrompt } from "@/components/auth";
import { DateTimeSlotPicker } from "@/components/booking/date-time-slot-picker";
import {
  BookingDetailsDrawer,
  bookingDetailsIncomplete,
  bookingDetailsRequiredMissing,
} from "@/components/booking-details/booking-details-drawer.client";
import {
  type TicketAnswerValues,
  type TicketQuestionGroup,
  TicketQuestionsDrawer,
  ticketAnswerErrors,
} from "@/components/booking-details/ticket-questions.client";
import { AttendeeTagPicker } from "@/components/events/attendee-tag-picker";
import { GuestAttendeeForm } from "@/components/events/guest-attendee-form.client";
import { DeferredAnswersDrawer } from "@/components/profile/deferred-answers-drawer.client";
import { useVideoAutoplay } from "@/components/providers/video-autoplay-provider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import Link from "@/components/ui/link";
import { Spinner } from "@/components/ui/spinner";
import type { MediaData } from "@/db/schema/hiive";
import {
  PackCoveredBadge,
  PackEligibilityNote,
} from "@/features/packs/components/pack-eligibility";
import { PackTenderSelector } from "@/features/packs/components/pack-tender-selector";
import {
  holdingCovering,
  packEligibilityFor,
  type ViewerPackHolding,
} from "@/features/packs/lib/eligibility";
import {
  ActionTimeoutError,
  withActionTimeout,
} from "@/features/payments/lib/async";
import { PushNudge } from "@/features/push-nudge";
import { setAuthAttribution } from "@/lib/analytics/auth-attribution";
import {
  captureClientEvent,
  captureIdentifiedClientEvent,
} from "@/lib/analytics/client";
import {
  type EventEntryAttribution,
  resolveEventEntry,
} from "@/lib/analytics/entry-point";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import {
  CHECKOUT_RESUME_PARAM,
  CHECKOUT_RESUME_VALUE,
  withoutCheckoutResumeMarker,
} from "@/lib/auth-redirect";
import type { BookingFieldModes } from "@/lib/booking-fields";
import {
  clearCheckoutIntent,
  consumeCheckoutIntent,
  stashCheckoutIntent,
} from "@/lib/checkout-intent";
import type { EventDeferredProfileAnswer } from "@/lib/event-access";
import { eventBookingDeadline } from "@/lib/events/booking-deadline";
import {
  guestListTicketMessage,
  isTicketVisibleOnEvent,
} from "@/lib/events/guest-list-visibility";
import type { TicketQuestion } from "@/lib/events/ticket-questions";
import { firstRealDisplayName } from "@/lib/identity/display-name";
import { createClientLogger } from "@/lib/logging/client";
import type { MediaAssetSlim } from "@/lib/media";
import type { PricingPolicy } from "@/lib/pricing/types";
import { createRandomId } from "@/lib/random-id";
import { cn } from "@/lib/utils";
import { bookingPassAvailability } from "./event-booking-availability";
import {
  formatEventBookingWindow,
  formatEventDate,
  formatEventTime,
  formatEventTimeRange,
  isSameEventDay,
  resolveEventTimeZone,
  toEventDateKey,
} from "./event-booking-time";
import { GuestSeatShare } from "./guest-seat-share.client";
import {
  isCompleteTicketAssignment,
  isGuestTicketAssignment,
} from "./ticket-assignment";
import {
  BalancedTicketNote,
  type PassBalance,
  WaitlistAppCard,
  WaitlistHeadStartNote,
  WaitlistPlaceNote,
} from "./waitlist-notes";

const logger = createClientLogger(
  "features/hiives/components/event-booking-hub/event-booking-page/event-booking-page.client",
);

const SETTLEMENT_CURRENCY = "INR";
/** Mirrors `getEventViewerRestriction` in `@/lib/event-access`. */
const GUEST_SIGN_IN_RESTRICTION = "Sign in to book this event.";
const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>();
type RatePreview = { rate: number; date: string; fetchedAtIso: string };

const formatCurrency = (amountMinor: number, currency: string) => {
  const cacheKey = currency;
  let formatter = CURRENCY_FORMATTERS.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: currency === "INR" ? 0 : 2,
      maximumFractionDigits: currency === "INR" ? 0 : 2,
    });
    CURRENCY_FORMATTERS.set(cacheKey, formatter);
  }
  return formatter.format(amountMinor / 100);
};

/**
 * The line under an approval-only ticket. It leads with the request — that is
 * what the person is doing — and treats a card hold as how it's sent.
 */
function joinRequestNote(
  request: OccurrencePass["joinRequest"],
  priceLabel: string,
  cardHold: boolean,
): string {
  if (request?.status === "approved") {
    if (request.hold === "held")
      return `You're approved. Your ${priceLabel} hold is charged the moment a spot opens for you.`;
    if (request.hold === "charged") return "You're approved and booked.";
    return "You're approved. Spots go to whoever books first, so book now.";
  }
  if (request?.status === "pending") {
    if (request.hold === "unpaid")
      return `Your request to join isn't sent yet. Finish it by holding ${priceLabel} on your card. You're charged only if the host approves you.`;
    if (request.hold === "held")
      return `Request sent. The host is deciding. ${priceLabel} is held on your card and charged only if you're approved.`;
    return "Request sent. The host is deciding; you'll pay only once you're approved.";
  }
  if (request?.status === "declined")
    return request.reviewNote ?? "The host couldn't fit you in for this date.";
  return cardHold
    ? `The host picks who comes. Request to join and hold ${priceLabel} on your card; you're charged only if you're approved.`
    : "The host picks who comes. Request to join; you'll pay only once you're approved.";
}

function GuestListClaimButton({
  eligible,
  onClaim,
  disabled,
  className,
}: {
  eligible: boolean | null | undefined;
  onClaim: () => void;
  disabled: boolean;
  className: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={className}
      onClick={onClaim}
      disabled={disabled || eligible === false}
    >
      {eligible === true
        ? "Claim"
        : eligible === false
          ? "Invite only"
          : "Continue"}
    </Button>
  );
}

const FREE_PASS_SELF_ONLY_MESSAGE =
  "Free tickets can only be claimed once and only for yourself.";
const FREE_PASS_SELF_ASSIGNMENT_MESSAGE =
  "Free tickets can only be assigned to your account.";
const FREE_PASS_ALREADY_ASSIGNED_MESSAGE =
  "You're already assigned to attend this event, so this free ticket can't be claimed again.";
const SELF_ALREADY_ASSIGNED_MESSAGE =
  "You're already assigned to attend this event.";

interface OccurrencePass {
  occurrencePassId: number;
  passTypeId: number;
  name: string;
  slug: string;
  description: string | null;
  attendeesPerPass: number;
  priceAmountMinor: number;
  pricingPolicy?: PricingPolicy | null;
  standardPriceAmountMinor: number;
  tierPriceAmountMinor: number | null;
  isDeposit: boolean;
  totalPriceMinor: number | null;
  currency: string;
  capacity: number | null;
  reserved: number;
  remaining: number | null;
  isActive: boolean;
  inviteOnly: boolean;
  /** Only people the host approved can book; everyone else requests. */
  requiresApproval?: boolean;
  /** Requesting holds the price on the card; charged only on approval. */
  cardHold?: boolean;
  joinRequest?: {
    id: number;
    status: "pending" | "approved" | "declined" | "withdrawn" | "canceled";
    reviewNote: string | null;
    cardHold?: boolean;
    /** On a card-hold ticket: "unpaid" hasn't reached the host yet. */
    hold?: "unpaid" | "held" | "charged" | "released" | null;
  } | null;
  linkOnly?: boolean;
  hideFromNonGuests?: boolean;
  guestListEligible?: boolean | null;
  /** The organiser's extra checkout questions for this ticket. */
  bookingQuestions?: TicketQuestion[];
  waitlistEnabled: boolean;
  waitlistState: {
    entryId: number;
    status: "active" | "offered";
    offerExpiresAt: string | null;
    position?: number | null;
  } | null;
  waitlistMode?: "notify" | "hold";
  /** Present on balanced tickets: what the viewer's side can do right now. */
  balance?: PassBalance | null;
  waitlistHeadStartUntil?: string | null;
  paymentCollection?: "online" | "at_venue";
  saleStartsAt: string | Date | null;
  saleEndsAt: string | Date | null;
  maxPerBooking: number | null;
}

interface BookingOccurrence {
  id: number;
  startTime: string | Date;
  endTime: string | Date;
  slotLabel: string | null;
  timezone: string | null;
  capacity: number | null;
  status: string;
  bookingOpensAt: string | Date | null;
  bookingClosesAt: string | Date | null;
  reserved: number;
  remaining: number | null;
  passes: OccurrencePass[];
}

interface SessionUser {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  username?: string | null;
}

interface HiiveSummary {
  id: number;
  name: string;
  slug: string;
  logoImage: MediaAssetSlim | null;
}

const optionalDetailsSkippedKey = (hiiveSlug: string, eventSlug: string) =>
  `hiive:booking-details-optional-skipped:${hiiveSlug}/${eventSlug}`;

/** Storage can throw (private mode, blocked site data); a miss just re-asks. */
function readOptionalDetailsSkipped(hiiveSlug: string, eventSlug: string) {
  try {
    return (
      window.localStorage.getItem(
        optionalDetailsSkippedKey(hiiveSlug, eventSlug),
      ) === "1"
    );
  } catch {
    return false;
  }
}

function rememberOptionalDetailsSkipped(hiiveSlug: string, eventSlug: string) {
  try {
    window.localStorage.setItem(
      optionalDetailsSkippedKey(hiiveSlug, eventSlug),
      "1",
    );
  } catch {
    // Not remembered: the optional questions are offered again next time.
  }
}

interface EventSummary {
  id: number;
  name: string;
  description: string;
  pattern: string;
  bannerMedia: MediaData[];
  venueName: string;
  address: string;
  // Null when the event withholds its exact location; the booking page
  // shows the venue name and address, never a pin.
  latitude: number | null;
  longitude: number | null;
  geohash: string;
  highlightStandsOut: string | null;
  highlightExperience: string | null;
  highlightSpecialAttractions: string | null;
  highlightPerfectFor: string | null;
  allowedGenders: Array<"male" | "female" | "other">;
  thingsToKnow: string[];
  amenities: string[];
  termsAndConditions: Array<{ title: string; description?: string | null }>;
  allowedTierIds: number[];
  /** Owner opt-out. When false, every seat must go to a Hiive account. */
  guestAttendeesEnabled: boolean;
  /** Extra booking questions; name and email are always asked. */
  bookingFields: BookingFieldModes;
}

interface EventBookingPageClientProps {
  hiive: HiiveSummary;
  event: EventSummary;
  occurrences: BookingOccurrence[];
  bookingRestriction?: string;
  canBookEvent: boolean;
  /**
   * Profile questions this event needs answered before a booking can be
   * created — asked here rather than refused. See `DeferredAnswersDrawer`.
   */
  deferredProfileAnswers: EventDeferredProfileAnswer[];
  allowedTierNames: string[];
  sessionUser?: SessionUser;
  hiiveSlug: string;
  eventSlug: string;
  /** What this hiive sells, so a ticket row can say a pack reaches it. */
  packs: PackForSaleRow[];
  /** The viewer's usable holdings, loaded once for the whole ticket list. */
  viewerPackHoldings: ViewerPackHolding[];
  /**
   * Occurrences the viewer already holds a ticket for. On those dates a new
   * ticket is never defaulted to them — checkout refuses a second ticket for
   * the same person, so pre-filling their name only set up a dead end.
   */
  viewerTicketedOccurrenceIds?: number[];
}

/**
 * One seat, in one of three states: empty, assigned to a Hiive account, or
 * assigned to a guest by name and email.
 *
 * The states are kept apart by which fields are set rather than by a tag,
 * because the row's own rendering already branches on `userId` and adding a
 * discriminant here would mean touching every existing read. The wire format
 * IS tagged — see the payload mapping in `handleSubmit`.
 */
type AttendeeInput = {
  seatKey?: string;
  userId?: string;
  displayName?: string;
  handle?: string;
  avatarUrl?: string | null;
  /** Set together, and only when `userId` is absent. */
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  /** Only on a balanced ticket, where the buyer states the guest's side. */
  guestGender?: "male" | "female";
  /** A tagged member's side on a balanced ticket, from their profile. */
  memberSide?: "male" | "female" | null;
};

const isGuestAttendee = isGuestTicketAssignment;

/** Identifies one seat while its "Assign to me" check is in flight. */
const selfSeatKey = (passId: number, index: number) => `${passId}-${index}`;

type SelectedPassState = {
  quantity: number;
  attendees: AttendeeInput[];
  waitlistEntryId: number | null;
};

/**
 * What a signed-out viewer had in their basket when the checkout drawer opened.
 * Quantities only: attendee tagging needs a session, so a guest's seats are all
 * empty anyway, and the ids here are server ids that survive a reload.
 */
type EventCheckoutIntent = {
  occurrenceId: number;
  passes: Array<{ occurrencePassId: number; quantity: number }>;
};

/**
 * `idle` — nothing to restore.
 * `locating` — waiting for the occurrence effects to settle on the saved date.
 * `filling` — occurrence is selected; write the saved quantities back.
 * `submitting` — selection restored; hand off to the normal submit path.
 * `done` — never runs again this mount.
 */
type ResumePhase = "idle" | "locating" | "filling" | "submitting" | "done";

let attendeeSeatKeyCounter = 0;

const createSeatKey = () => `attendee-seat-${attendeeSeatKeyCounter++}`;

/**
 * Booking only for the other side: the viewer's own side of a balanced ticket
 * is full but the other side has room, so they can still buy tickets for,
 * say, women without taking one themselves. Returns how many they can add
 * (null = no limit), or undefined when this doesn't apply.
 */
function othersOnlyCapFor(
  pass: NormalizedPass,
  signedIn: boolean,
): number | null | undefined {
  const balance = pass.balance;
  if (balance?.mode !== "balanced" || !signedIn) return;
  const side = balance.viewerSide;
  if (side !== "male" && side !== "female") return;
  // Their own side still has room: the normal Add applies.
  if (pass.remaining !== 0) return;
  const otherOpen = balance.open[side === "male" ? "female" : "male"];
  if (otherOpen !== null && otherOpen <= 0) return;
  return otherOpen;
}

const createEmptyAttendee = (): AttendeeInput => ({
  seatKey: createSeatKey(),
});

const isSelfOnlyPass = ({
  attendeesPerPass,
  priceAmountMinor,
}: {
  attendeesPerPass: number;
  priceAmountMinor: number;
}) => priceAmountMinor === 0 && attendeesPerPass > 0;

const getInitials = (value: string) =>
  value
    .split(" ", 2)
    .map((segment) => segment[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);

type NormalizedPass = OccurrencePass & {
  priceLabel: string;
  standardPriceLabel: string;
  saleStarts: Date | null;
  saleEnds: Date | null;
};

type NormalizedOccurrence = Omit<
  BookingOccurrence,
  "passes" | "startTime" | "endTime" | "bookingOpensAt" | "bookingClosesAt"
> & {
  start: Date;
  end: Date;
  bookingOpensAt: Date | null;
  bookingClosesAt: Date | null;
  passes: NormalizedPass[];
};

const toDate = (value: Date | string | null | undefined) =>
  value ? (value instanceof Date ? value : new Date(value)) : null;

const dateFromKey = (dateKey: string) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
};

const getOccurrenceUnavailableReason = (
  occurrence: NormalizedOccurrence,
  now: Date | null,
) => {
  if (!now) return null;
  // Defensive: a malformed occurrence must not be treated as bookable and must
  // not throw when we later format its window. This is the guard for the
  // production crash that read startTime off an undefined occurrence.
  if (!occurrence?.start || Number.isNaN(occurrence.start.valueOf())) {
    return "This session can't be booked right now.";
  }
  if (
    now >=
    eventBookingDeadline({
      ...occurrence,
      startTime: occurrence.start,
      endTime: occurrence.end,
    })
  ) {
    return "Bookings for this session are closed.";
  }
  if (occurrence.bookingOpensAt && occurrence.bookingOpensAt > now) {
    try {
      return `Bookings open ${formatEventBookingWindow(
        occurrence.bookingOpensAt,
        resolveEventTimeZone(occurrence.timezone),
      )}.`;
    } catch {
      return "Bookings are not open yet.";
    }
  }
  return null;
};

const getDateUnavailableReason = (
  occurrences: NormalizedOccurrence[],
  now: Date | null,
) => {
  if (!now) return null;
  if (!occurrences || occurrences.length === 0)
    return "No sessions are available on this date.";
  // Filter out malformed occurrences before deciding bookability.
  const validOccurrences = occurrences.filter(
    (o) => o?.start && !Number.isNaN(o.start.valueOf()),
  );
  if (validOccurrences.length === 0)
    return "No sessions are available on this date.";
  if (
    validOccurrences.some(
      (occurrence) => !getOccurrenceUnavailableReason(occurrence, now),
    )
  ) {
    return null;
  }
  if (validOccurrences.every((occurrence) => occurrence.end <= now)) {
    return "This date is in the past and can't be booked.";
  }
  return (
    getOccurrenceUnavailableReason(validOccurrences[0], now) ??
    "No sessions on this date can be booked."
  );
};

export function EventBookingPageClient({
  hiive,
  event,
  occurrences,
  bookingRestriction,
  canBookEvent,
  deferredProfileAnswers,
  allowedTierNames,
  sessionUser,
  hiiveSlug,
  eventSlug,
  packs,
  viewerPackHoldings,
  viewerTicketedOccurrenceIds = [],
}: EventBookingPageClientProps) {
  const router = useRouter();
  const { openAuthPrompt } = useAuthPrompt();
  const { setManualPause } = useVideoAutoplay();
  const attendeeSectionRef = useRef<HTMLDivElement | null>(null);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<
    number | null
  >(null);
  const [isAttendeeInfoOpen, setIsAttendeeInfoOpen] = useState(false);
  const [selectedPasses, setSelectedPasses] = useState<
    Record<number, SelectedPassState>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [packPreview, setPackPreview] = useState<PackTenderPreview | null>(
    null,
  );
  // `null` means pay with money. Never defaulted to the pack: see
  // PackTenderSelector — spending someone's credits because it was possible is
  // spending their money without asking.
  const [packHoldingId, setPackHoldingId] = useState<number | null>(null);
  const [_successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [isWaitlistPending, startWaitlistTransition] = useTransition();
  /**
   * Which seat is mid-"Assign to me", as `passId-index`.
   *
   * The handler awaits an eligibility check on the server, and until this
   * existed the button showed nothing for the whole round trip. On a slow
   * connection that reads as a dead button: people tapped it repeatedly, and
   * the first response then landed on a seat they had already moved past.
   */
  const [assigningSelfSeat, setAssigningSelfSeat] = useState<string | null>(
    null,
  );
  const requiresEligibilityChecks =
    event.allowedTierIds.length > 0 || event.allowedGenders.length > 0;
  // Guests are impossible on a restricted event regardless of the owner's flag:
  // there is no membership row to read for someone with no profile, so a tier
  // gate over a guest is not a lenient check, it is no check. Mirrors rule 2.
  //
  // A balanced ticket is fine: the guest form asks the buyer for the guest's
  // side, since there is no profile to read it from.
  const guestAttendeesAllowed =
    event.guestAttendeesEnabled && !requiresEligibilityChecks;
  const duplicateAttendeeMessage =
    "Each ticket must be assigned to a different person.";
  const trackedStartBookingRef = useRef(false);
  const bookingIntentKey = `${hiiveSlug}/${eventSlug}`;
  const [resumeIntent, setResumeIntent] = useState<EventCheckoutIntent | null>(
    null,
  );
  const [resumePhase, setResumePhase] = useState<ResumePhase>("idle");
  const resumeCheckedRef = useRef(false);
  const [answersDrawerOpen, setAnswersDrawerOpen] = useState(false);
  /**
   * The booking details drawer: name and email, plus the organiser's extra
   * questions. `bookingDetails` is what was on file when checkout decided to
   * ask; the drawer is keyed on each opening so its fields never shift.
   */
  const [bookingDetails, setBookingDetails] =
    useState<BookingContactDetails | null>(null);
  const [bookingDetailsOpen, setBookingDetailsOpen] = useState(false);
  const [bookingDetailsKey, setBookingDetailsKey] = useState(0);
  /** Asked and answered this visit. A ref for the same reason as below. */
  const bookingDetailsReadyRef = useRef(false);
  const bookingDetailsResumeRef = useRef<(() => void) | null>(null);
  /**
   * The outstanding questions have been answered acceptably in this session.
   *
   * A ref rather than state because the drawer's `onAnswered` re-enters
   * `handleSubmit` in the same tick: a state read there would still see the
   * pre-answer value and re-open the drawer it just closed. Nothing renders
   * from it — closing the drawer is what re-renders — so state would only be a
   * second copy of the same fact.
   */
  const deferredAnswersSatisfiedRef = useRef(false);
  /** What to re-run once the questions are answered. See `deferredAnswersBlock`. */
  const deferredAnswersResumeRef = useRef<(() => void) | null>(null);
  /**
   * Stop and ask, if there is anything left to ask. Returns whether the caller
   * should abandon this attempt and wait for an answer.
   *
   * Every path that consults the viewer's gender goes through here, because the
   * server refuses all of them and a refusal with no question in front of it is
   * the dead end this replaces. There are four, and the assignment one is the
   * earliest: tapping "Assign to me" calls `validateBookingAttendee`, so
   * without this the viewer was told "You're not eligible for this event."
   * before they could ever reach Checkout.
   *
   * `resume` is what to do once they have answered — different for each caller,
   * which is why it is passed in rather than assumed to be the submit.
   */
  const deferredAnswersBlock = (resume: () => void) => {
    if (deferredAnswersSatisfiedRef.current) return false;
    if (deferredProfileAnswers.length === 0) return false;
    deferredAnswersResumeRef.current = resume;
    setAnswersDrawerOpen(true);
    return true;
  };

  /**
   * Stop and ask for missing booking details, if any. Same contract as
   * `deferredAnswersBlock`: returns whether the caller should wait.
   *
   * Always asks the server what is on file rather than trusting the session
   * snapshot — a detail added on another screen must not be asked for again.
   * The booking actions re-check on their own, so a failed read here blocks
   * nothing: it lets checkout run and the server answer.
   */
  const bookingDetailsBlock = (resume: () => void) => {
    if (bookingDetailsReadyRef.current) return false;
    bookingDetailsResumeRef.current = resume;
    void getBookingContactDetails()
      .then((result) => {
        if (
          !result.ok ||
          !bookingDetailsIncomplete(result.data, event.bookingFields) ||
          // Optional questions are asked once per event on this device: a
          // person who skipped them is not asked again on every booking.
          (!bookingDetailsRequiredMissing(result.data, event.bookingFields) &&
            readOptionalDetailsSkipped(hiiveSlug, eventSlug))
        ) {
          bookingDetailsReadyRef.current = true;
          const next = bookingDetailsResumeRef.current;
          bookingDetailsResumeRef.current = null;
          next?.();
          return;
        }
        setBookingDetails(result.data);
        setBookingDetailsKey((key) => key + 1);
        setBookingDetailsOpen(true);
      })
      .catch(() => {
        bookingDetailsReadyRef.current = true;
        const next = bookingDetailsResumeRef.current;
        bookingDetailsResumeRef.current = null;
        next?.();
      });
    return true;
  };

  /**
   * The organiser's per-ticket questions. Answers live in a ref for the same
   * reason as `deferredAnswersSatisfiedRef`: `onComplete` re-enters
   * `handleSubmit` on the same tick. `ticketQuestionsAskedRef` remembers which
   * tickets were shown the drawer, so optional questions are asked once per
   * visit rather than on every tap of Checkout.
   */
  const [ticketQuestionsOpen, setTicketQuestionsOpen] = useState(false);
  const [ticketQuestionsKey, setTicketQuestionsKey] = useState(0);
  const [ticketQuestionGroups, setTicketQuestionGroups] = useState<
    TicketQuestionGroup[]
  >([]);
  const ticketAnswersRef = useRef<Record<number, TicketAnswerValues>>({});
  const ticketQuestionsAskedRef = useRef(new Set<number>());
  const ticketQuestionsResumeRef = useRef<(() => void) | null>(null);
  const ticketQuestionsBlock = (
    passes: OccurrencePass[],
    resume: () => void,
  ) => {
    // An approval-only ticket's questions were answered with the request.
    const groups = passes
      .filter(
        (pass) =>
          !pass.requiresApproval && (pass.bookingQuestions?.length ?? 0) > 0,
      )
      .map((pass) => ({
        passTypeId: pass.passTypeId,
        name: pass.name,
        questions: pass.bookingQuestions ?? [],
      }));
    const outstanding = groups.some(
      (group) =>
        !ticketQuestionsAskedRef.current.has(group.passTypeId) ||
        Object.keys(
          ticketAnswerErrors(
            group.questions,
            ticketAnswersRef.current[group.passTypeId] ?? {},
          ),
        ).length > 0,
    );
    if (!outstanding) return false;
    ticketQuestionsResumeRef.current = resume;
    setTicketQuestionGroups(groups);
    setTicketQuestionsKey((key) => key + 1);
    setTicketQuestionsOpen(true);
    return true;
  };

  // The hand-off cover is derived, never stored. Two sources, both of which
  // clear themselves:
  //
  //   the `resume` search param — the gate saying "a basket is coming back
  //   here". It is in the URL, so it is right on a server render, right after
  //   a client navigation, and gone the moment we route away.
  //
  //   `isPending` — React's own transition state, true from the reserve call
  //   until the payment route commits.
  //
  // An earlier version latched this in `useState` with a 20s failsafe. That is
  // what left a stuck "Finishing your booking" behind after cancelling a
  // booking and coming back: the flag outlived the navigation it described.
  // Stored UI state that mirrors navigation will always drift; derive it.
  const searchParams = useSearchParams();
  const resumeRequested =
    searchParams.get(CHECKOUT_RESUME_PARAM) === CHECKOUT_RESUME_VALUE;
  const handoffInFlight =
    isPending || (resumeRequested && resumePhase !== "done");

  // This is local UI state, so clear it synchronously without a server-backed
  // router transition. Native history is integrated with Next's search params.
  // Keeping the phase guard above means the cover also drops in the same render
  // if a browser delays reflecting the URL change.
  const exitResume = useCallback(() => {
    if (!resumeRequested) return;
    const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.history.replaceState(
      null,
      "",
      withoutCheckoutResumeMarker(currentHref),
    );
  }, [resumeRequested]);

  // Did this mount come back from a checkout sign-in? Read once, before the
  // occurrence effects have had a chance to pick a default date, so the saved
  // occurrence wins rather than fighting them a frame later.
  useEffect(() => {
    // Order matters. Native sign-in never reloads the document, so this
    // component can survive the entire round-trip — checking the ref first
    // burned the one-shot on the signed-out pass and the viewer came back to
    // an untouched ticket picker with the sign-in banner still on it.
    if (!sessionUser) return;
    if (resumeCheckedRef.current) return;
    resumeCheckedRef.current = true;

    const intent = consumeCheckoutIntent<EventCheckoutIntent>({
      kind: "event-booking",
      key: bookingIntentKey,
    });
    if (!intent?.passes?.length) {
      // Marker on the URL but no basket behind it — a reload, a cleared
      // storage, a shared link. Show the picker rather than a stuck spinner.
      exitResume();
      return;
    }

    captureIdentifiedClientEvent(
      ANALYTICS_EVENTS.CHECKOUT_AUTH_RESUMED,
      sessionUser.id,
      {
        surface: "event",
        hiive_slug: hiiveSlug,
        event_slug: eventSlug,
        occurrence_id: intent.occurrenceId,
      },
    );
    setResumeIntent(intent);
    setResumePhase("locating");
  }, [bookingIntentKey, eventSlug, exitResume, hiiveSlug, sessionUser]);

  useEffect(() => {
    setManualPause(false);
    return () => {
      setManualPause(false);
    };
  }, [setManualPause]);

  /**
   * Frozen for the session on first touch, then stamped on every funnel event
   * this page emits. Most people reach this page without ever opening the event
   * page — a shared link points straight here — and until the entry attribution
   * existed the dashboard could only report that as "booking starts are 257% of
   * views".
   *
   * Resolved inside the effect below rather than during render: it reads and
   * writes `sessionStorage`, which is neither available on the server nor safe
   * to touch from a render pass. Every later reader is a user-triggered
   * handler, so the ref is always populated by the time they run.
   */
  const entryAttributionRef = useRef<EventEntryAttribution | null>(null);

  useEffect(() => {
    if (trackedStartBookingRef.current) return;
    trackedStartBookingRef.current = true;
    entryAttributionRef.current = resolveEventEntry({
      eventSlug,
      route: "booking_page",
    });
    captureIdentifiedClientEvent(
      ANALYTICS_EVENTS.EVENT_BOOKING_STARTED,
      sessionUser?.id,
      {
        event_id: event.id,
        hiive_id: hiive.id,
        hiive_slug: hiive.slug,
        event_slug: eventSlug,
        event_name: event.name,
        ...entryAttributionRef.current,
      },
    );
  }, [event.id, event.name, eventSlug, hiive.id, hiive.slug, sessionUser?.id]);

  const [now, setNow] = useState<Date | null>(null);
  const [ratePreviewByCurrency, setRatePreviewByCurrency] = useState<
    Record<string, RatePreview>
  >({});

  useEffect(() => {
    setNow(new Date());
    const timer = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  const normalizedOccurrences = useMemo<NormalizedOccurrence[]>(() => {
    if (!Array.isArray(occurrences)) return [];
    try {
      return occurrences
        .filter(
          (occurrence) =>
            occurrence != null &&
            occurrence.status === "scheduled" &&
            occurrence.startTime != null &&
            occurrence.endTime != null,
        )
        .map((occurrence) => {
          // Defensive: occurrence may be malformed if DB returned a sparse row
          // or a cached payload was truncated. Reading startTime off undefined
          // is the exact crash reported on /book.
          const start = toDate(occurrence?.startTime);
          const end = toDate(occurrence?.endTime);
          const bookingOpens = toDate(occurrence?.bookingOpensAt);
          const bookingCloses = toDate(occurrence?.bookingClosesAt);

          if (
            !start ||
            !end ||
            Number.isNaN(start.valueOf()) ||
            Number.isNaN(end.valueOf())
          ) {
            return null;
          }

          const passes = Array.isArray(occurrence.passes)
            ? occurrence.passes.filter(
                (pass) => pass && isTicketVisibleOnEvent(pass),
              )
            : [];

          return {
            ...occurrence,
            start,
            end,
            bookingOpensAt: bookingOpens,
            bookingClosesAt: bookingCloses,
            passes: passes.map((pass) => ({
              ...pass,
              priceLabel: formatCurrency(pass.priceAmountMinor, pass.currency),
              standardPriceLabel: formatCurrency(
                pass.standardPriceAmountMinor,
                pass.currency,
              ),
              saleStarts: toDate(pass.saleStartsAt),
              saleEnds: toDate(pass.saleEndsAt),
            })),
          };
        })
        .filter(
          (occurrence): occurrence is NonNullable<typeof occurrence> =>
            occurrence !== null,
        )
        .sort(
          (first, second) => first.start.getTime() - second.start.getTime(),
        );
    } catch {
      // Never crash the booking page on a malformed occurrence.
      return [];
    }
  }, [occurrences]);

  const occurrencesByDate = useMemo(() => {
    const grouped = new Map<string, NormalizedOccurrence[]>();
    normalizedOccurrences.forEach((occurrence) => {
      if (!occurrence?.start || Number.isNaN(occurrence.start.valueOf()))
        return;
      try {
        const key = toEventDateKey(
          occurrence.start,
          resolveEventTimeZone(occurrence.timezone),
        );
        if (!grouped.has(key)) {
          grouped.set(key, [occurrence]);
        } else {
          grouped.get(key)?.push(occurrence);
        }
      } catch {
        // Ignore malformed dates — one bad occurrence must not crash the picker.
      }
    });
    for (const list of grouped.values()) {
      list.sort((a, b) => {
        const aTime = a.start?.getTime() ?? 0;
        const bTime = b.start?.getTime() ?? 0;
        return aTime - bTime;
      });
    }
    return grouped;
  }, [normalizedOccurrences]);

  const dateKeys = useMemo(
    () => Array.from(occurrencesByDate.keys()).sort(),
    [occurrencesByDate],
  );

  const bookableDateKeys = useMemo(
    () =>
      dateKeys.filter((dateKey) => {
        const occurrencesOnDate = occurrencesByDate.get(dateKey) ?? [];
        return occurrencesOnDate.some(
          (occurrence) => !getOccurrenceUnavailableReason(occurrence, now),
        );
      }),
    [dateKeys, occurrencesByDate, now],
  );

  useEffect(() => {
    if (dateKeys.length === 0) {
      setSelectedDateKey(null);
      return;
    }
    setSelectedDateKey((current) => {
      if (
        current &&
        dateKeys.includes(current) &&
        (bookableDateKeys.length === 0 || bookableDateKeys.includes(current))
      ) {
        return current;
      }
      return bookableDateKeys[0] ?? dateKeys[0];
    });
  }, [bookableDateKeys, dateKeys]);

  const occurrencesForSelectedDate = useMemo(() => {
    if (!selectedDateKey) {
      return [];
    }
    return occurrencesByDate.get(selectedDateKey) ?? [];
  }, [occurrencesByDate, selectedDateKey]);

  useEffect(() => {
    if (occurrencesForSelectedDate.length === 0) {
      setSelectedOccurrenceId(null);
      setSelectedPasses({});
      return;
    }

    const preferredOccurrence =
      occurrencesForSelectedDate.find(
        (occurrence) => !getOccurrenceUnavailableReason(occurrence, now),
      ) ?? occurrencesForSelectedDate[0];

    if (!selectedOccurrenceId) {
      setSelectedOccurrenceId(preferredOccurrence.id);
      setSelectedPasses({});
      return;
    }

    const currentOccurrence =
      occurrencesForSelectedDate.find(
        (occurrence) => occurrence.id === selectedOccurrenceId,
      ) ?? null;
    const stillExists = Boolean(currentOccurrence);
    const currentUnavailable =
      currentOccurrence !== null
        ? Boolean(getOccurrenceUnavailableReason(currentOccurrence, now))
        : false;

    if (!stillExists || currentUnavailable) {
      setSelectedOccurrenceId(preferredOccurrence.id);
      setSelectedPasses({});
    }
  }, [occurrencesForSelectedDate, selectedOccurrenceId, now]);

  useEffect(() => {
    setSelectedPasses({});
    setError(null);
    setSuccessMessage(null);
  }, []);

  const selectedOccurrence =
    selectedOccurrenceId !== null
      ? (normalizedOccurrences.find(
          (occurrence) => occurrence.id === selectedOccurrenceId,
        ) ?? null)
      : null;

  // A one-time event can still run several times that day (see
  // lib/events/event-pattern), so the picker follows the sessions, never the
  // pattern alone: hiding it used to book every guest into the first batch.
  const hasSessionChoice =
    event.pattern !== "single" || normalizedOccurrences.length > 1;

  const viewerHoldsTicketForSelectedOccurrence =
    selectedOccurrenceId !== null &&
    viewerTicketedOccurrenceIds.includes(selectedOccurrenceId);

  const selectedOccurrenceUnavailableReason = selectedOccurrence
    ? getOccurrenceUnavailableReason(selectedOccurrence, now)
    : null;
  const selectedOccurrenceIsBookable = Boolean(
    selectedOccurrence && !selectedOccurrenceUnavailableReason,
  );
  const previewCurrencies = useMemo(() => {
    if (!selectedOccurrence) return [];
    return Array.from(
      new Set(
        selectedOccurrence.passes
          .map((pass) => pass.currency.toUpperCase())
          .filter((code) => code !== SETTLEMENT_CURRENCY),
      ),
    );
  }, [selectedOccurrence]);

  useEffect(() => {
    if (previewCurrencies.length === 0) return;
    let canceled = false;

    for (const currencyCode of previewCurrencies) {
      if (ratePreviewByCurrency[currencyCode]) continue;
      getExchangeRate({ from: currencyCode }).then((result) => {
        if (canceled || !result.ok) return;
        setRatePreviewByCurrency((current) => ({
          ...current,
          [currencyCode]: {
            rate: result.data.rate.rate,
            date: result.data.rate.date,
            fetchedAtIso: result.data.rate.fetchedAtIso,
          },
        }));
      });
    }

    return () => {
      canceled = true;
    };
  }, [previewCurrencies, ratePreviewByCurrency]);

  const passInfoMap = useMemo(() => {
    const map = new Map<
      number,
      {
        occurrenceId: number;
        pass: NormalizedPass;
      }
    >();

    normalizedOccurrences.forEach((occurrence) => {
      occurrence.passes.forEach((pass) => {
        map.set(pass.occurrencePassId, {
          occurrenceId: occurrence.id,
          pass,
        });
      });
    });

    return map;
  }, [normalizedOccurrences]);

  // Any surfaced failure hands the page back — the viewer needs to see the
  // message and their restored selection, not a spinner.
  useEffect(() => {
    if (error) exitResume();
  }, [error, exitResume]);

  // Put the basket back together after a checkout sign-in.
  //
  // Two hops on purpose. The date/occurrence effects above own
  // `selectedDateKey`/`selectedOccurrenceId` and wipe `selectedPasses` whenever
  // the occurrence changes, so writing both in one pass would have the
  // selection cleared out from under it. Hop one steers those effects onto the
  // saved occurrence; hop two, once they have settled there, writes the passes.
  useEffect(() => {
    if (resumePhase !== "locating" && resumePhase !== "filling") return;
    if (!resumeIntent) return;

    const target = normalizedOccurrences.find(
      (occurrence) => occurrence.id === resumeIntent.occurrenceId,
    );
    // Sold out, cancelled, or the window closed while they were signing in —
    // leave them on the page with the normal availability messaging.
    if (!target) {
      setResumeIntent(null);
      setResumePhase("done");
      exitResume();
      return;
    }

    if (selectedOccurrenceId !== target.id) {
      setSelectedDateKey(
        toEventDateKey(target.start, resolveEventTimeZone(target.timezone)),
      );
      setSelectedOccurrenceId(target.id);
      setResumePhase("filling");
      return;
    }

    const restored: Record<number, SelectedPassState> = {};
    let selfSeatTaken = false;
    /** Tickets that no longer fit now we know who they are. */
    const dropped: Array<{ name: string; kept: number; saved: number }> = [];

    for (const line of resumeIntent.passes) {
      const passInfo = passInfoMap.get(line.occurrencePassId);
      if (!passInfo || passInfo.occurrenceId !== target.id) continue;
      const pass = passInfo.pass;
      // What this person can add now that their side is known: the other
      // side's room when their own side is full (and none of the tickets is
      // theirs), otherwise what's left for them. A balanced ticket can have
      // filled, or turned out to be full for them, while they signed in.
      const othersCap = othersOnlyCapFor(pass, Boolean(sessionUser));
      const seatLimit = othersCap === undefined ? pass.remaining : othersCap;
      const ticketLimit =
        seatLimit === null || pass.attendeesPerPass <= 0
          ? Number.POSITIVE_INFINITY
          : Math.floor(seatLimit / pass.attendeesPerPass);
      const saved = Math.max(0, Math.floor(line.quantity));
      const quantity = Math.min(saved, ticketLimit);
      if (quantity < saved)
        dropped.push({ name: pass.name, kept: quantity, saved });
      if (quantity === 0) continue;

      const seatCount = quantity * passInfo.pass.attendeesPerPass;
      const attendees: AttendeeInput[] = Array.from({ length: seatCount }, () =>
        createEmptyAttendee(),
      );

      // The viewer just proved who they are, so seat one is theirs — exactly
      // what tapping "+" would have done had they been signed in all along.
      if (!selfSeatTaken && attendees.length > 0 && othersCap === undefined) {
        attendees[0] = {
          seatKey: attendees[0].seatKey,
          userId: sessionUser?.id,
          displayName: sessionUser?.name ?? "Me",
          handle: sessionUser?.username ?? undefined,
          avatarUrl: sessionUser?.image ?? undefined,
        };
        selfSeatTaken = true;
      }

      restored[line.occurrencePassId] = {
        quantity,
        attendees,
        waitlistEntryId: null,
      };
    }

    setResumeIntent(null);

    // Say what changed rather than leave a basket that silently shrank.
    for (const { name, kept, saved } of dropped) {
      toast.info(
        kept === 0
          ? `${name} filled up while you were signing in, so your ${saved === 1 ? "ticket wasn't" : `${saved} tickets weren't`} kept.`
          : `Only ${kept} of your ${saved} ${name} tickets ${kept === 1 ? "is" : "are"} still available, so we kept ${kept}.`,
      );
    }

    if (Object.keys(restored).length === 0) {
      setResumePhase("done");
      exitResume();
      return;
    }

    setSelectedPasses(restored);
    setResumePhase("submitting");
  }, [
    exitResume,
    normalizedOccurrences,
    passInfoMap,
    resumeIntent,
    resumePhase,
    selectedOccurrenceId,
    sessionUser,
  ]);

  const selectedPassEntries = useMemo(
    () =>
      Object.entries(selectedPasses).filter(([, value]) => value.quantity > 0),
    [selectedPasses],
  );

  const seatBearingPassEntries = useMemo(
    () =>
      selectedPassEntries.filter(([passId]) => {
        const passInfo = passInfoMap.get(Number(passId));
        return Boolean(passInfo && passInfo.pass.attendeesPerPass > 0);
      }),
    [passInfoMap, selectedPassEntries],
  );

  const taggedUserIds = useMemo(() => {
    const ids = new Set<string>();
    Object.entries(selectedPasses).forEach(([passId, selection]) => {
      const passInfo = passInfoMap.get(Number(passId));
      if (!passInfo || passInfo.pass.attendeesPerPass <= 0) {
        return;
      }
      selection.attendees.forEach((attendee) => {
        if (attendee.userId) {
          ids.add(attendee.userId);
        }
      });
    });
    return ids;
  }, [passInfoMap, selectedPasses]);

  const totalSelectedAttendees = seatBearingPassEntries.reduce(
    (sum, [passId, selection]) => {
      const passInfo = passInfoMap.get(Number(passId));
      if (!passInfo) {
        return sum;
      }
      return sum + selection.attendees.length;
    },
    0,
  );

  const totalAmountMinor = selectedPassEntries.reduce(
    (sum, [passId, selection]) => {
      const passInfo = passInfoMap.get(Number(passId));
      if (!passInfo) {
        return sum;
      }
      return sum + selection.quantity * passInfo.pass.priceAmountMinor;
    },
    0,
  );
  const standardTotalAmountMinor = selectedPassEntries.reduce(
    (sum, [passId, selection]) => {
      const passInfo = passInfoMap.get(Number(passId));
      if (!passInfo) {
        return sum;
      }
      return sum + selection.quantity * passInfo.pass.standardPriceAmountMinor;
    },
    0,
  );
  const selectedDiscountMinor = Math.max(
    standardTotalAmountMinor - totalAmountMinor,
    0,
  );

  /**
   * What this basket could be paid with, requoted whenever the basket changes.
   *
   * Quote only — `createEventBooking` resolves the cover again inside its own
   * transaction, because this answer goes stale the moment another device
   * spends a credit. A failure here leaves `packPreview` null and the selector
   * renders nothing: a pack that cannot be offered costs the sheet its pack
   * row, never the ability to book.
   */
  const packQuoteKey = selectedOccurrence
    ? JSON.stringify([
        selectedOccurrence.id,
        selectedPassEntries.map(([passId, selection]) => [
          Number(passId),
          selection.quantity,
        ]),
      ])
    : null;

  useEffect(() => {
    if (!packQuoteKey || !sessionUser?.id) {
      setPackPreview(null);
      return;
    }
    const [occurrenceId, passes] = JSON.parse(packQuoteKey) as [
      number,
      Array<[number, number]>,
    ];
    if (passes.length === 0) {
      setPackPreview(null);
      return;
    }

    let cancelled = false;
    // Debounced: the quote is keyed on the basket, and a member tapping + four
    // times to buy four tickets would otherwise fire four server actions whose
    // first three answers are already stale by the time they land.
    const timer = setTimeout(() => {
      previewEventPackTender({
        occurrenceId,
        passes: passes.map(([occurrencePassId, quantity]) => ({
          occurrencePassId,
          quantity,
        })),
      })
        .then((result) => {
          if (cancelled) return;
          setPackPreview(result.ok ? result.data : null);
        })
        .catch(() => {
          if (!cancelled) setPackPreview(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [packQuoteKey, sessionUser?.id]);

  // A pack the basket no longer qualifies for must not stay selected: add a
  // ticket the pack cannot cover and the choice has to fall back to money, or
  // the payload would carry a holding the commit path will refuse.
  const offeredHoldingId = packPreview?.option?.holdingId ?? null;
  useEffect(() => {
    setPackHoldingId((current) =>
      current != null && current !== offeredHoldingId ? null : current,
    );
  }, [offeredHoldingId]);

  /*
   * The event-level answer, for the note above the ticket list.
   *
   * Measured against the cheapest pass on the occurrence in view, so the
   * percentage is a floor: every dearer pass the pack also covers saves more.
   * Deposit passes are excluded because a pack can never pay for one (B17).
   */
  const eventPackLine = { eventId: event.id };
  const eventPackHolding = holdingCovering(viewerPackHoldings, eventPackLine);
  /*
   * Both ends of what a pack saves here, measured per pass.
   *
   * Per pass because scoping is per pass: a pack on the drop-in does not cover
   * the single-occupancy cabin, and averaging across passes it cannot pay for
   * would quote a saving nobody can get.
   */
  const eventPackPercent = (() => {
    const percents: number[] = [];
    for (const pass of selectedOccurrence?.passes ?? []) {
      if (
        pass.paymentCollection === "at_venue" ||
        pass.isDeposit ||
        pass.priceAmountMinor <= 0
      )
        continue;
      const eligible = packEligibilityFor(packs, {
        eventId: event.id,
        eventPassTypeId: pass.passTypeId,
        listPriceMinor: pass.priceAmountMinor,
      });
      if (eligible?.bestPercent != null) percents.push(eligible.bestPercent);
    }
    if (percents.length === 0) return null;
    return { low: Math.min(...percents), high: Math.max(...percents) };
  })();
  const eventPackEligibility = packEligibilityFor(packs, eventPackLine);

  const packCoversBooking =
    packHoldingId != null && packHoldingId === offeredHoldingId;
  const hasDepositSelection = selectedPassEntries.some(([passId]) => {
    const passInfo = passInfoMap.get(Number(passId));
    return Boolean(
      passInfo?.pass.isDeposit && passInfo.pass.totalPriceMinor != null,
    );
  });
  const settlementPreviewFor = (amountMinor: number, currencyCode: string) => {
    const code = currencyCode.toUpperCase();
    if (code === SETTLEMENT_CURRENCY) return null;
    const preview = ratePreviewByCurrency[code];
    return preview ? Math.round(amountMinor * preview.rate) : null;
  };
  const settlementPreviewLabelFor = (
    amountMinor: number,
    currencyCode: string,
  ) => {
    const convertedMinor = settlementPreviewFor(amountMinor, currencyCode);
    return convertedMinor == null
      ? null
      : formatCurrency(convertedMinor, SETTLEMENT_CURRENCY);
  };

  const totalPassesSelected = selectedPassEntries.reduce(
    (sum, [, selection]) => sum + selection.quantity,
    0,
  );

  const requiresAuthToBook = !sessionUser;
  // The server reports "Sign in to book this event." for every signed-out
  // viewer. On a public event that is the one restriction the checkout drawer
  // resolves on the spot, so it must not be rendered as a blocker. On a
  // tier-restricted event it is only the first of several, and promising "one
  // tap to continue" there would be a lie.
  //
  // A gender-restricted event is on the near side of that line now: signing in
  // IS enough to keep going, because the missing answer is then asked for
  // directly (`deferredProfileAnswers`) instead of sending the viewer to profile
  // settings. They may still be refused on the answer — but the promise that
  // sign-in is the next step, and the only one they have to go looking for, is
  // true.
  const guestSignInIsTheOnlyBlock =
    requiresAuthToBook &&
    bookingRestriction === GUEST_SIGN_IN_RESTRICTION &&
    event.allowedTierIds.length === 0;
  const canInteractWithPasses =
    (canBookEvent || requiresAuthToBook) &&
    (!bookingRestriction || requiresAuthToBook) &&
    selectedOccurrenceIsBookable &&
    !isPending &&
    !isWaitlistPending;
  /**
   * Sign-in from inside the booking flow. Two things make it different from
   * every other prompt in the app: the basket is saved first so the sign-in
   * does not cost the viewer their selection, and the `checkout` intent tells
   * the post-auth gate to hand them straight back here rather than into
   * onboarding. See `plans/guest-checkout-auth.md`.
   */
  const openBookingAuthPrompt = () => {
    if (selectedOccurrence && selectedPassEntries.length > 0) {
      stashCheckoutIntent<EventCheckoutIntent>({
        kind: "event-booking",
        key: bookingIntentKey,
        payload: {
          occurrenceId: selectedOccurrence.id,
          passes: selectedPassEntries.map(([passId, selection]) => ({
            occurrencePassId: Number(passId),
            quantity: selection.quantity,
          })),
        },
      });
    } else {
      // Nothing worth resuming — and a leftover record from an earlier attempt
      // must not fire on the way back.
      clearCheckoutIntent();
    }

    // Written before the prompt opens so it survives the OAuth round trip: the
    // Google and Apple buttons live in a global drawer and have no idea which
    // checkout summoned them, which is why `auth_provider_selected` could never
    // be attributed to an event.
    setAuthAttribution({
      surface: "event",
      hiive_slug: hiiveSlug,
      event_slug: eventSlug,
      occurrence_id: selectedOccurrence?.id ?? null,
      total_passes: totalPassesSelected,
    });

    captureClientEvent(ANALYTICS_EVENTS.CHECKOUT_AUTH_PROMPTED, {
      surface: "event",
      hiive_slug: hiiveSlug,
      event_slug: eventSlug,
      occurrence_id: selectedOccurrence?.id ?? null,
      total_passes: totalPassesSelected,
      ...entryAttributionRef.current,
    });

    if (guestSignInIsTheOnlyBlock) {
      openAuthPrompt({
        intent: "checkout",
        title: "Log in or sign up",
        description: "One tap to continue. You can finish your profile later.",
      });
      return;
    }

    openAuthPrompt({
      title: "Sign in to book",
      description: "Use your Hiive account to continue with this booking.",
    });
  };

  /**
   * Seat assignment while signed out.
   *
   * Every affordance on an unassigned seat needs a session: the member search
   * refuses server-side, "Assign to me" has nobody to assign, and guest
   * name/email cannot ride through the checkout hand-off (the stashed intent
   * carries quantities only). Disabling all three left the row looking broken
   * — a greyed button and a search that returns nothing.
   *
   * So they stay live and hand the viewer forward instead. The wording is
   * about the order of steps rather than about being logged out, because from
   * here it IS just the next step: the same drawer the Checkout button opens,
   * with the basket already stashed. Multi-seat baskets come back here to
   * finish assigning (the resume path only auto-submits once every seat is
   * filled), so this is a real promise rather than a redirect.
   */
  /**
   * Signed out, and trying to assign a seat.
   *
   * The guest details are deliberately NOT carried through the sign-in hand-off:
   * stashing somebody else's name and email in client storage across an auth
   * boundary is not a trade worth making for two fields. But the copy has to say
   * so. It used to read "you can assign each ticket right after", which promises
   * continuity the flow does not deliver — the fields are cleared, and the
   * person re-types them once per seat wondering what they did wrong.
   */
  const interceptAttendeeAssignment = () => {
    if (sessionUser) return false;
    toast.info("Sign in first — then add each guest's details.");
    openBookingAuthPrompt();
    return true;
  };

  const _seatsRemainingForOccurrence =
    selectedOccurrence?.remaining !== null && selectedOccurrence
      ? selectedOccurrence.remaining - totalSelectedAttendees
      : null;

  const resetSelection = () => {
    setSelectedPasses({});
    setError(null);
    setSuccessMessage(null);
  };

  const othersOnlyCap = (pass: NormalizedPass) =>
    othersOnlyCapFor(pass, Boolean(sessionUser));

  const handleAdjustQuantity = (
    passId: number,
    direction: "increase" | "decrease",
  ) => {
    const passNode = passInfoMap.get(passId);
    if (!passNode) {
      return;
    }

    const { occurrenceId, pass } = passNode;
    if (occurrenceId !== selectedOccurrenceId) {
      return;
    }

    if (!selectedOccurrence) {
      return;
    }

    // A balanced ticket can't say what this person may book until it knows
    // their side. Ask first; `onComplete` re-reads the page, which then shows
    // them "Add" or "Join waitlist" for their own side.
    if (
      direction === "increase" &&
      sessionUser &&
      pass.balance &&
      pass.balance.viewerSide === null &&
      bookingDetailsBlock(() => router.refresh())
    ) {
      return;
    }

    if (direction === "decrease") {
      const currentQuantity = selectedPasses[passId]?.quantity ?? 0;
      if (currentQuantity <= 0) {
        return;
      }
      const nextQuantity = currentQuantity - 1;
      captureClientEvent(
        ANALYTICS_EVENTS.EVENT_BOOKING_PASS_QUANTITY_ADJUSTED,
        {
          pass_id: pass.occurrencePassId,
          pass_name: pass.name,
          direction: "decrease",
          quantity: nextQuantity,
          hiive_slug: hiiveSlug,
          event_slug: eventSlug,
          occurrence_id: selectedOccurrenceId,
          ...entryAttributionRef.current,
        },
      );

      setSelectedPasses((prev) => {
        const current = prev[passId];
        if (!current) {
          return prev;
        }

        if (current.quantity <= 0) {
          return prev;
        }

        const newQuantity = current.quantity - 1;
        if (newQuantity <= 0) {
          const { [passId]: _removed, ...rest } = prev;
          return rest;
        }

        const requiredSeats = newQuantity * pass.attendeesPerPass;

        return {
          ...prev,
          [passId]: {
            quantity: newQuantity,
            attendees: current.attendees.slice(0, requiredSeats),
            waitlistEntryId: current.waitlistEntryId,
          },
        };
      });
      setError(null);
      return;
    }

    if (!canInteractWithPasses) {
      if (selectedOccurrenceUnavailableReason) {
        setError(selectedOccurrenceUnavailableReason);
        toast.error(selectedOccurrenceUnavailableReason);
      }
      return;
    }

    const interactionNow = now ?? new Date();
    const saleWindowActive =
      (!pass.saleStarts || pass.saleStarts <= interactionNow) &&
      (!pass.saleEnds || pass.saleEnds > interactionNow);

    if (!saleWindowActive && pass.waitlistState?.status !== "offered") {
      setError(`${pass.name} is not on sale right now.`);
      return;
    }

    const current = selectedPasses[passId] ?? {
      quantity: 0,
      attendees: [] as AttendeeInput[],
      waitlistEntryId: null,
    };
    const selfOnlyPass = isSelfOnlyPass(pass);

    if (selfOnlyPass && !sessionUser) {
      const message = "Sign in to claim this free ticket.";
      setError(message);
      openBookingAuthPrompt();
      return;
    }

    if (selfOnlyPass && current.quantity >= 1) {
      setError(FREE_PASS_SELF_ONLY_MESSAGE);
      toast.error(FREE_PASS_SELF_ONLY_MESSAGE);
      return;
    }

    if (selfOnlyPass && viewerHoldsTicketForSelectedOccurrence) {
      setError(FREE_PASS_ALREADY_ASSIGNED_MESSAGE);
      toast.error(FREE_PASS_ALREADY_ASSIGNED_MESSAGE);
      return;
    }

    if (
      selfOnlyPass &&
      sessionUser &&
      Object.entries(selectedPasses).some(([key, state]) => {
        if (Number(key) === passId) return false;
        const passInfo = passInfoMap.get(Number(key));
        if (!passInfo || passInfo.pass.attendeesPerPass <= 0) return false;
        return state.attendees.some(
          (attendee) => attendee.userId === sessionUser.id,
        );
      })
    ) {
      setError(FREE_PASS_ALREADY_ASSIGNED_MESSAGE);
      toast.error(FREE_PASS_ALREADY_ASSIGNED_MESSAGE);
      return;
    }

    if (pass.waitlistState?.status === "offered" && current.quantity >= 1) {
      setError(`You can only claim one offered ${pass.name} at a time.`);
      return;
    }

    const totalSeatsSelected = Object.entries(selectedPasses).reduce(
      (sum, [key, value]) =>
        sum + (Number(key) === passId ? 0 : value.attendees.length),
      0,
    );

    const seatsSelectedForPass = current.attendees.length;
    const claimedHoldSeats =
      current.waitlistEntryId || pass.waitlistState?.status === "offered"
        ? pass.attendeesPerPass
        : 0;

    if (
      selectedOccurrence.remaining !== null &&
      totalSeatsSelected +
        seatsSelectedForPass +
        pass.attendeesPerPass -
        claimedHoldSeats >
        selectedOccurrence.remaining
    ) {
      setError("Not enough spots remain for this occurrence.");
      return;
    }

    // Own side full: what's left to add is the other side's room.
    const othersCap = othersOnlyCap(pass);
    const passLimit = othersCap === undefined ? pass.remaining : othersCap;
    if (
      passLimit !== null &&
      seatsSelectedForPass + pass.attendeesPerPass - claimedHoldSeats >
        passLimit
    ) {
      setError(`${pass.name} does not have enough availability.`);
      return;
    }

    if (pass.maxPerBooking && current.quantity + 1 > pass.maxPerBooking) {
      setError(`${pass.name} is limited to ${pass.maxPerBooking} per booking.`);
      return;
    }

    const nextQuantity = current.quantity + 1;

    captureClientEvent(ANALYTICS_EVENTS.EVENT_BOOKING_PASS_QUANTITY_ADJUSTED, {
      pass_id: pass.occurrencePassId,
      pass_name: pass.name,
      direction: "increase",
      quantity: nextQuantity,
      hiive_slug: hiiveSlug,
      event_slug: eventSlug,
      occurrence_id: selectedOccurrenceId,
      ...entryAttributionRef.current,
    });

    setSelectedPasses((prev) => {
      const currentInCallback = prev[passId] ?? {
        quantity: 0,
        attendees: [] as AttendeeInput[],
        waitlistEntryId: null,
      };

      const newQuantity = currentInCallback.quantity + 1;
      const requiredSeats = newQuantity * pass.attendeesPerPass;

      const nextAttendees: AttendeeInput[] = Array.from(
        { length: requiredSeats },
        (_, index) =>
          currentInCallback.attendees[index] ?? createEmptyAttendee(),
      );

      // Default the first open seat to the viewer — unless they already hold a
      // ticket for this date, where checkout would refuse them anyway, or
      // their own side is full and these tickets are for the other side.
      if (
        sessionUser &&
        pass.attendeesPerPass > 0 &&
        !viewerHoldsTicketForSelectedOccurrence &&
        othersCap === undefined
      ) {
        const hasSelf = nextAttendees.some(
          (attendee) => attendee.userId === sessionUser.id,
        );
        // Only ever an EMPTY seat: seat 1 may already hold someone they tagged.
        const openSeatIndex = nextAttendees.findIndex(
          (attendee) => !attendee.userId && !isGuestAttendee(attendee),
        );
        if (!hasSelf && openSeatIndex !== -1) {
          // Check if self is already assigned in another pass
          const isInOtherPass = Object.entries(prev).some(([key, state]) => {
            if (Number(key) === passId) return false;
            const pInfo = passInfoMap.get(Number(key));
            if (!pInfo || pInfo.pass.attendeesPerPass <= 0) return false;
            return state.attendees.some((a) => a.userId === sessionUser.id);
          });

          if (!isInOtherPass) {
            nextAttendees[openSeatIndex] = {
              seatKey: nextAttendees[openSeatIndex]?.seatKey ?? createSeatKey(),
              userId: sessionUser.id,
              displayName: firstRealDisplayName(sessionUser.name) ?? "Me",
              handle: sessionUser.username ?? undefined,
              avatarUrl: sessionUser.image ?? undefined,
            };
          }
        }
      }

      setError(null);

      return {
        ...prev,
        [passId]: {
          quantity: newQuantity,
          attendees: nextAttendees,
          waitlistEntryId:
            currentInCallback.waitlistEntryId ??
            (pass.waitlistState?.status === "offered"
              ? pass.waitlistState.entryId
              : null),
        },
      };
    });
  };

  /**
   * `?ticket=<slug>` — a ticket's printed QR code. Select that ticket once,
   * on the default date, and bring it into view; a basket the person already
   * has (or one coming back from sign-in) is never overwritten.
   */
  const ticketParam = searchParams.get("ticket");
  const ticketPreselectRef = useRef(false);
  const preselectTicketRef = useRef<() => void>(() => {});
  preselectTicketRef.current = () => {
    if (ticketPreselectRef.current || !ticketParam || !selectedOccurrence)
      return;
    ticketPreselectRef.current = true;
    if (resumeRequested || Object.keys(selectedPasses).length > 0) return;
    const pass = selectedOccurrence.passes.find(
      (candidate) => candidate.slug === ticketParam,
    );
    if (!pass) return;
    handleAdjustQuantity(pass.occurrencePassId, "increase");
    window.requestAnimationFrame(() =>
      document
        .getElementById(`booking-pass-${pass.occurrencePassId}`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" }),
    );
  };
  useEffect(() => {
    if (selectedOccurrence) preselectTicketRef.current();
  }, [selectedOccurrence]);

  const updateAttendeeAt = (
    passId: number,
    seatIndex: number,
    updater: (current: AttendeeInput) => AttendeeInput,
  ) => {
    setSelectedPasses((prev) => {
      const current = prev[passId];
      if (!current) {
        return prev;
      }

      const attendees = current.attendees.slice();
      const existing = attendees[seatIndex] ?? createEmptyAttendee();
      attendees[seatIndex] = {
        ...updater(existing),
        seatKey: existing.seatKey ?? createSeatKey(),
      };

      return {
        ...prev,
        [passId]: {
          ...current,
          attendees,
        },
      };
    });
  };

  const handleSelectTaggedAttendee = (
    passId: number,
    index: number,
    suggestion: BookingAttendeeSuggestion,
  ) => {
    const passInfo = passInfoMap.get(passId);
    if (passInfo && isSelfOnlyPass(passInfo.pass)) {
      setError(FREE_PASS_SELF_ASSIGNMENT_MESSAGE);
      toast.error(FREE_PASS_SELF_ASSIGNMENT_MESSAGE);
      return;
    }

    if (isUserTaggedElsewhere(suggestion.id, passId, index)) {
      const message =
        suggestion.id === sessionUser?.id
          ? SELF_ALREADY_ASSIGNED_MESSAGE
          : `${suggestion.label} is already assigned to attend this event.`;
      setError(message);
      toast.error(message);
      return;
    }
    updateAttendeeAt(passId, index, () => ({
      userId: suggestion.id,
      displayName: suggestion.label,
      handle: suggestion.handle ?? undefined,
      avatarUrl: suggestion.avatarUrl ?? null,
      memberSide: suggestion.side ?? null,
    }));
    setError(null);
  };

  const handleAssignGuest = (
    passId: number,
    index: number,
    values: {
      guestName: string;
      guestEmail: string;
      guestPhone?: string;
      guestGender?: "male" | "female";
    },
  ) => {
    const passInfo = passInfoMap.get(passId);
    if (passInfo && isSelfOnlyPass(passInfo.pass)) {
      setError(FREE_PASS_SELF_ASSIGNMENT_MESSAGE);
      toast.error(FREE_PASS_SELF_ASSIGNMENT_MESSAGE);
      return;
    }

    // Same rule the server enforces (rule 5), checked here so the person finds
    // out while the fields are still in front of them rather than at checkout.
    const email = values.guestEmail.trim().toLowerCase();
    if (isGuestEmailUsedElsewhere(email, passId, index)) {
      const message = `${values.guestName} already has one of these tickets.`;
      setError(message);
      toast.error(message);
      return;
    }

    updateAttendeeAt(passId, index, () => ({
      guestName: values.guestName,
      guestEmail: email,
      guestPhone: values.guestPhone,
      guestGender: values.guestGender,
      displayName: values.guestName,
    }));
    setError(null);
  };

  /**
   * The sides a balanced ticket can't take another seat on, for one empty
   * seat: each side's free spots minus the seats already in this basket on
   * that side (you on yours, guests on the side chosen for them, tagged
   * members on the side from their profile).
   */
  const fullSidesForSeat = (
    pass: NormalizedPass,
    seatIndex: number,
  ): Array<"male" | "female"> => {
    if (!pass.balance) return [];
    const used = { male: 0, female: 0 };
    const viewerSide = pass.balance.viewerSide;
    (selectedPasses[pass.occurrencePassId]?.attendees ?? []).forEach(
      (seat, idx) => {
        if (idx === seatIndex) return;
        const side =
          seat.guestGender ??
          (seat.userId && seat.userId === sessionUser?.id
            ? viewerSide
            : seat.memberSide);
        if (side === "male" || side === "female") used[side] += 1;
      },
    );
    return (["male", "female"] as const).filter((side) => {
      const open = pass.balance?.open[side];
      return open != null && open - used[side] <= 0;
    });
  };

  const isGuestEmailUsedElsewhere = (
    email: string,
    targetPassId: number,
    seatIndex: number,
  ) => {
    for (const [passKey, state] of Object.entries(selectedPasses)) {
      const numericPassId = Number(passKey);
      const passInfo = passInfoMap.get(numericPassId);
      if (!passInfo || passInfo.pass.attendeesPerPass <= 0) continue;
      for (let idx = 0; idx < state.attendees.length; idx += 1) {
        if (numericPassId === targetPassId && idx === seatIndex) continue;
        if (state.attendees[idx]?.guestEmail?.toLowerCase() === email) {
          return true;
        }
      }
    }
    return false;
  };

  const handleClearTaggedAttendee = (passId: number, index: number) => {
    const passInfo = passInfoMap.get(passId);
    if (passInfo && isSelfOnlyPass(passInfo.pass)) {
      return;
    }

    updateAttendeeAt(passId, index, () => createEmptyAttendee());
  };

  const handleAssignSelf = async (passId: number, index: number) => {
    if (!sessionUser) {
      interceptAttendeeAssignment();
      return;
    }

    // Before `validateBookingAttendee`, which judges the stored gender and
    // would refuse outright. This is the first tap on a gender-restricted
    // event, so it is where the question has to be asked.
    if (
      deferredAnswersBlock(() => {
        void handleAssignSelf(passId, index);
      })
    ) {
      return;
    }

    if (
      viewerHoldsTicketForSelectedOccurrence ||
      isUserTaggedElsewhere(sessionUser.id, passId, index)
    ) {
      setError(SELF_ALREADY_ASSIGNED_MESSAGE);
      toast.error(SELF_ALREADY_ASSIGNED_MESSAGE);
      return;
    }

    const seatKey = selfSeatKey(passId, index);
    // One flight per tap. A second tap while the check is out would otherwise
    // race the first and could land on a seat that has since changed.
    if (assigningSelfSeat) return;
    setAssigningSelfSeat(seatKey);

    try {
      if (requiresEligibilityChecks || selectedOccurrence) {
        const validation = await validateBookingAttendee({
          userId: sessionUser.id,
          hiiveId: hiive.id,
          allowedTierIds: event.allowedTierIds,
          allowedGenders: event.allowedGenders,
          occurrenceId: selectedOccurrence?.id,
        });

        if (!validation.ok) {
          setError(validation.message);
          toast.error(validation.message);
          return;
        }
      }

      updateAttendeeAt(passId, index, () => ({
        userId: sessionUser.id,
        displayName: firstRealDisplayName(sessionUser.name) ?? "Me",
        avatarUrl: sessionUser.image ?? undefined,
        handle: sessionUser.username ?? undefined,
      }));
      setError(null);
    } catch (err) {
      // A thrown server action used to leave the button silent forever. Say so
      // instead — the tap has to end in either a filled seat or a reason.
      logger.error("assign_self_failed", { error: err });
      const message =
        "Couldn't assign this ticket to you. Check your connection and try again.";
      setError(message);
      toast.error(message);
    } finally {
      setAssigningSelfSeat(null);
    }
  };

  const isUserTaggedElsewhere = (
    userId: string,
    targetPassId: number,
    seatIndex: number,
  ) => {
    for (const [passKey, state] of Object.entries(selectedPasses)) {
      const numericPassId = Number(passKey);
      const passInfo = passInfoMap.get(numericPassId);
      if (!passInfo || passInfo.pass.attendeesPerPass <= 0) {
        continue;
      }
      for (let idx = 0; idx < state.attendees.length; idx += 1) {
        const attendee = state.attendees[idx];
        if (
          attendee.userId === userId &&
          (numericPassId !== targetPassId || idx !== seatIndex)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const hasSeatBearingSelection = seatBearingPassEntries.length > 0;

  const isSubmitDisabled =
    !selectedOccurrence ||
    Boolean(selectedOccurrenceUnavailableReason) ||
    (!requiresAuthToBook && (!canBookEvent || !!bookingRestriction)) ||
    totalPassesSelected === 0 ||
    isPending;

  const handleSubmit = () => {
    const blockSubmit = (message: string) => {
      captureClientEvent(ANALYTICS_EVENTS.EVENT_BOOKING_BLOCKED, {
        hiive_slug: hiiveSlug,
        event_slug: eventSlug,
        occurrence_id: selectedOccurrence?.id ?? null,
        reason: message,
        ...entryAttributionRef.current,
      });
      setError(message);
      toast.error(message);
    };

    if (!selectedOccurrence) {
      blockSubmit("Select an occurrence to continue.");
      return;
    }

    if (selectedOccurrenceUnavailableReason) {
      blockSubmit(selectedOccurrenceUnavailableReason);
      return;
    }

    // The fork, reported as one event with two outcomes. Counting only the
    // prompted side made the auth step look like a cliff: members who sail
    // straight through contributed nothing to either number.
    captureIdentifiedClientEvent(
      ANALYTICS_EVENTS.CHECKOUT_AUTH_GATE_EVALUATED,
      sessionUser?.id,
      {
        surface: "event",
        outcome: sessionUser ? "already_signed_in" : "prompted",
        hiive_slug: hiiveSlug,
        event_slug: eventSlug,
        occurrence_id: selectedOccurrence.id,
        total_passes: totalPassesSelected,
        ...entryAttributionRef.current,
      },
    );

    if (!sessionUser) {
      openBookingAuthPrompt();
      return;
    }

    if (!canBookEvent) {
      blockSubmit(
        bookingRestriction ?? "You are not allowed to book this event.",
      );
      return;
    }

    if (bookingRestriction) {
      blockSubmit(bookingRestriction);
      return;
    }

    if (selectedPassEntries.length === 0) {
      blockSubmit("Select at least one pass to continue.");
      return;
    }

    // Deliberately after the basket check, so nobody is asked their gender to
    // buy nothing, and before every validation below, because the answer can
    // still end this attempt.
    if (deferredAnswersBlock(handleSubmit)) return;
    if (bookingDetailsBlock(handleSubmit)) return;
    if (
      ticketQuestionsBlock(
        selectedPassEntries.flatMap(([passId]) => {
          const pass = passInfoMap.get(Number(passId))?.pass;
          return pass ? [pass] : [];
        }),
        handleSubmit,
      )
    )
      return;

    const seenUserIds = new Set<string>();

    for (const [passId, selection] of selectedPassEntries) {
      const passInfo = passInfoMap.get(Number(passId));
      if (!passInfo) {
        blockSubmit("Unable to determine pass information.");
        return;
      }

      const requiredSeats = selection.quantity * passInfo.pass.attendeesPerPass;
      const selfOnlyPass = isSelfOnlyPass(passInfo.pass);

      const requiresSeats = passInfo.pass.attendeesPerPass > 0;

      if (selfOnlyPass && requiredSeats !== 1) {
        blockSubmit(FREE_PASS_SELF_ONLY_MESSAGE);
        return;
      }

      if (requiresSeats) {
        if (selection.attendees.length !== requiredSeats) {
          blockSubmit("Assign each selected ticket before checking out.");
          return;
        }

        for (const attendee of selection.attendees) {
          if (!isCompleteTicketAssignment(attendee)) {
            blockSubmit(
              requiresEligibilityChecks
                ? "Choose an eligible attendee for every ticket."
                : "Choose an attendee for every ticket.",
            );
            return;
          }

          if (selfOnlyPass && attendee.userId !== sessionUser?.id) {
            blockSubmit(FREE_PASS_SELF_ASSIGNMENT_MESSAGE);
            return;
          }

          // Guest seats are valid assignments too. The old loop unconditionally
          // required `userId`, so the row rendered "Assigned" and then Checkout
          // rejected the exact same guest as missing.
          if (attendee.userId) {
            if (seenUserIds.has(attendee.userId)) {
              blockSubmit(duplicateAttendeeMessage);
              return;
            }
            seenUserIds.add(attendee.userId);
          }
        }
      }
    }

    const checkoutAttemptId = createRandomId();
    const checkoutStartedAt = performance.now();
    const payload = {
      checkoutAttemptId,
      occurrenceId: selectedOccurrence.id,
      ...(packCoversBooking ? { packHoldingId } : {}),
      passes: selectedPassEntries.map(([passId, selection]) => {
        const passInfo = passInfoMap.get(Number(passId));
        if (!passInfo) {
          throw new Error(
            "Missing pass metadata while shaping the booking payload",
          );
        }
        return {
          occurrencePassId: passInfo.pass.occurrencePassId,
          waitlistEntryId: selection.waitlistEntryId ?? undefined,
          quantity: selection.quantity,
          attendeesPerPass: passInfo.pass.attendeesPerPass,
          ...(passInfo.pass.bookingQuestions?.length
            ? { answers: ticketAnswersRef.current[passInfo.pass.passTypeId] }
            : {}),
          attendees: selection.attendees.map((attendee) =>
            isGuestAttendee(attendee)
              ? {
                  kind: "guest" as const,
                  guestName: attendee.guestName ?? "",
                  guestEmail: attendee.guestEmail ?? "",
                  // Omitted rather than sent empty: the schema's `.strict()`
                  // accepts an absent optional but rejects a blank string.
                  ...(attendee.guestPhone
                    ? { guestPhone: attendee.guestPhone }
                    : {}),
                  ...(attendee.guestGender
                    ? { guestGender: attendee.guestGender }
                    : {}),
                }
              : { kind: "user" as const, userId: attendee.userId ?? "" },
          ),
        };
      }),
    };

    /**
     * snake_case, and not by preference.
     *
     * These three events used to send `hiiveSlug` / `eventSlug` in camelCase
     * while every query in `posthog-query.ts` filters on `event_slug`. The
     * result was silent: 71 submissions in ten days, none of them visible to
     * the event funnel, so the checkout-click stage read as zero on a page that
     * was plainly getting checkout clicks.
     */
    const bookingAnalyticsBase = {
      checkout_attempt_id: checkoutAttemptId,
      hiive_slug: hiiveSlug,
      event_slug: eventSlug,
      occurrence_id: selectedOccurrence.id,
      total_amount_minor: totalAmountMinor,
      currency: currencyForTotal,
      total_passes: totalPassesSelected,
      total_attendees: totalSelectedAttendees,
      ...entryAttributionRef.current,
    };

    captureIdentifiedClientEvent(
      ANALYTICS_EVENTS.EVENT_BOOKING_SUBMITTED,
      sessionUser.id,
      {
        ...bookingAnalyticsBase,
        selected_passes: selectedPassEntries.map(([passId, selection]) => ({
          pass_id: Number(passId),
          quantity: selection.quantity,
        })),
      },
    );

    setError(null);
    setSuccessMessage(null);

    // An async transition, not a sync one wrapping a floating promise: React
    // holds `isPending` true across the await AND across the navigation
    // started inside it, so the cover and the button's pending state end
    // exactly when the payment route commits. That is what makes the cover
    // safe to derive rather than latch.
    startTransition(async () => {
      let timedOut = false;
      let requestPhase = "request_sent";
      const recordCheckpoint = (
        checkpoint: string,
        details: Record<string, unknown> = {},
      ) => {
        try {
          captureIdentifiedClientEvent(
            ANALYTICS_EVENTS.EVENT_BOOKING_CHECKOUT_CHECKPOINT_RECORDED,
            sessionUser.id,
            {
              ...bookingAnalyticsBase,
              ...details,
              checkpoint,
              request_phase: requestPhase,
              elapsed_ms: Math.round(performance.now() - checkoutStartedAt),
              online: navigator.onLine,
              visibility_state: document.visibilityState,
            },
          );
        } catch {
          // Observability must never prevent a booking request or navigation.
        }
      };
      const onVisibility = () => recordCheckpoint("visibility_changed");
      const onPageHide = () => recordCheckpoint("document_hidden");
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("pagehide", onPageHide);
      recordCheckpoint("request_sent");
      const request = createEventBooking(payload).then((result) => {
        if (timedOut) {
          recordCheckpoint("late_server_response_received", {
            ok: result.ok,
            ...(result.ok ? { booking_id: result.data.bookingId } : {}),
          });
        }
        return result;
      });
      await withActionTimeout(
        request,
        20_000,
        "Checkout is taking longer than expected. Check your wallet for a pending booking before trying again.",
      )
        .then((result) => {
          requestPhase = "response_received";
          recordCheckpoint("server_response_received", {
            ok: result.ok,
            ...(result.ok ? { booking_id: result.data.bookingId } : {}),
          });
          if (!result.ok) {
            captureIdentifiedClientEvent(
              ANALYTICS_EVENTS.EVENT_BOOKING_FAILED,
              sessionUser.id,
              { ...bookingAnalyticsBase, reason: result.message },
            );
            setError(result.message);
            toast.error(result.message);
            // A balanced ticket may have just learned this person's side at
            // the details step, and their side is the one that's full. Re-read
            // the page so the ticket turns into "Join waitlist" right here,
            // rather than leaving them at a refusal with nowhere to go.
            // Any other refusal keeps the basket: the fix is usually one seat.
            // A balanced ticket still re-reads its sides, so a side that just
            // filled shows as full on the seat being changed.
            if (result.balanceWaitlisted) {
              setSelectedPasses({});
              router.refresh();
            } else if (
              normalizedOccurrences.some((occurrence) =>
                occurrence.passes.some((pass) => pass.balance),
              )
            ) {
              router.refresh();
            }
            return;
          }

          const bookingId = result.data.bookingId;
          captureIdentifiedClientEvent(
            ANALYTICS_EVENTS.EVENT_BOOKING_SUCCEEDED,
            sessionUser.id,
            { ...bookingAnalyticsBase, booking_id: bookingId },
          );
          // No success toast and no reset before the push. Both used to fire
          // while this page was still on screen, so a tap on Checkout read as
          // three separate events — the basket emptying itself, a toast, then
          // the page finally changing a beat later. The payment screen IS the
          // confirmation; raise the cover and go there in one move.
          router.push(
            `/hiives/${hiiveSlug}/events/${eventSlug}/book/payment?bookingId=${bookingId}`,
          );
          requestPhase = "navigation_requested";
          recordCheckpoint("payment_navigation_requested", {
            booking_id: bookingId,
          });
          // Behind the cover, so the emptying basket is never seen.
          resetSelection();
        })
        .catch((err) => {
          timedOut = err instanceof ActionTimeoutError;
          requestPhase = timedOut ? "response_timed_out" : "request_rejected";
          recordCheckpoint(requestPhase, {
            outcome: timedOut ? "unknown" : "error",
            timeout_ms: timedOut ? err.timeoutMs : undefined,
          });
          logger.error("createEventBooking error", err);
          const message =
            err instanceof Error
              ? err.message
              : "Something went wrong while creating the booking.";
          captureIdentifiedClientEvent(
            ANALYTICS_EVENTS.EVENT_BOOKING_FAILED,
            sessionUser.id,
            { ...bookingAnalyticsBase, reason: message },
          );
          setError(message);
          toast.error(message);
        })
        .finally(() => {
          document.removeEventListener("visibilitychange", onVisibility);
          window.removeEventListener("pagehide", onPageHide);
        });
    });
  };

  // Last leg of the resumed checkout: the basket is back, so finish the tap the
  // viewer already made and drop them on the payment page.
  //
  // Only when every seat is accounted for. A multi-seat selection needs
  // attendees tagged, and tagging needs people this viewer is connected to —
  // auto-submitting would just bounce off `blockSubmit`. In that case the
  // selection is simply left restored and they carry on by hand.
  //
  // Everything it needs is read through a ref rather than a dependency:
  // `handleSubmit` and the derived selection are rebuilt on every render, and
  // the phase flip — not a dependency change — is what makes this fire exactly
  // once.
  const resumeSubmitRef = useRef<() => boolean>(() => false);
  resumeSubmitRef.current = () => {
    const everySeatAssigned = selectedPassEntries.every(
      ([passId, selection]) => {
        const passInfo = passInfoMap.get(Number(passId));
        if (!passInfo || passInfo.pass.attendeesPerPass <= 0) return true;
        const requiredSeats =
          selection.quantity * passInfo.pass.attendeesPerPass;
        return (
          selection.attendees.length === requiredSeats &&
          // Same rule as `handleSubmit`. When this asked for a `userId` a
          // restored selection with email guests silently refused to resume,
          // so signing in mid-checkout dropped people back onto a full basket
          // that would not submit itself.
          selection.attendees.every(isCompleteTicketAssignment)
        );
      },
    );

    if (
      selectedPassEntries.length === 0 ||
      !everySeatAssigned ||
      isSubmitDisabled
    ) {
      return false;
    }

    // Returning false here is what drops the "Finishing your booking" cover:
    // the question has to be readable, and it cannot be read through a
    // full-screen spinner. The basket stays restored underneath it, so
    // answering resumes the same checkout rather than restarting it.
    if (deferredAnswersBlock(handleSubmit)) return false;
    if (bookingDetailsBlock(handleSubmit)) return false;

    handleSubmit();
    return true;
  };

  useEffect(() => {
    if (resumePhase !== "submitting") return;
    setResumePhase("done");
    // Only a real submit keeps the cover up — a reserve call ends in a
    // navigation to payment. When the seats need tagging by hand, hand the
    // page back instead of parking the viewer behind a spinner.
    if (!resumeSubmitRef.current()) {
      exitResume();
    }
  }, [resumePhase, exitResume]);

  const handleJoinWaitlist = (occurrencePassId: number) => {
    if (!sessionUser) {
      openBookingAuthPrompt();
      return;
    }

    if (!canBookEvent) {
      const message =
        bookingRestriction ?? "You are not allowed to join this waitlist.";
      captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_JOIN_FAILED, {
        hiive_slug: hiiveSlug,
        event_slug: eventSlug,
        occurrence_pass_id: occurrencePassId,
        ...entryAttributionRef.current,
        reason: message,
      });
      setError(message);
      toast.error(message);
      return;
    }

    // A waitlist seat becomes a ticket, so it inherits the same eligibility —
    // ask here too rather than let somebody hold a place they cannot take up.
    if (
      deferredAnswersBlock(() => {
        handleJoinWaitlist(occurrencePassId);
      })
    ) {
      return;
    }

    // Same details as checkout, asked before joining: the email is where the
    // "a spot opened" alert goes, and a balanced ticket needs the gender to
    // know which line this person is in.
    if (
      bookingDetailsBlock(() => {
        handleJoinWaitlist(occurrencePassId);
      })
    ) {
      return;
    }

    if (bookingRestriction) {
      captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_JOIN_FAILED, {
        hiive_slug: hiiveSlug,
        event_slug: eventSlug,
        occurrence_pass_id: occurrencePassId,
        ...entryAttributionRef.current,
        reason: bookingRestriction,
      });
      setError(bookingRestriction);
      toast.error(bookingRestriction);
      return;
    }

    captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_JOIN_ATTEMPTED, {
      hiive_slug: hiiveSlug,
      event_slug: eventSlug,
      occurrence_pass_id: occurrencePassId,
      ...entryAttributionRef.current,
    });
    setError(null);
    startWaitlistTransition(() => {
      joinEventWaitlist({ occurrencePassId })
        .then((result) => {
          if (!result.ok) {
            captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_JOIN_FAILED, {
              hiive_slug: hiiveSlug,
              event_slug: eventSlug,
              occurrence_pass_id: occurrencePassId,
              ...entryAttributionRef.current,
              reason: result.message,
            });
            setError(result.message);
            toast.error(result.message);
            return;
          }

          captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_JOIN_SUCCEEDED, {
            hiiveSlug,
            eventSlug,
            occurrencePassId,
          });
          toast.success(result.message ?? "Joined the waitlist.");
          router.refresh();
        })
        .catch((err) => {
          const message =
            err instanceof Error ? err.message : "Failed to join the waitlist.";
          captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_JOIN_FAILED, {
            hiiveSlug,
            eventSlug,
            occurrencePassId,
            reason: message,
          });
          setError(message);
          toast.error(message);
        });
    });
  };

  const handleLeaveWaitlist = (occurrencePassId: number) => {
    captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_LEAVE_ATTEMPTED, {
      hiive_slug: hiiveSlug,
      event_slug: eventSlug,
      occurrence_pass_id: occurrencePassId,
      ...entryAttributionRef.current,
    });
    setError(null);
    startWaitlistTransition(() => {
      leaveEventWaitlist({ occurrencePassId })
        .then((result) => {
          if (!result.ok) {
            captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_LEAVE_FAILED, {
              hiive_slug: hiiveSlug,
              event_slug: eventSlug,
              occurrence_pass_id: occurrencePassId,
              ...entryAttributionRef.current,
              reason: result.message,
            });
            setError(result.message);
            toast.error(result.message);
            return;
          }

          captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_LEAVE_SUCCEEDED, {
            hiiveSlug,
            eventSlug,
            occurrencePassId,
          });
          toast.success(result.message ?? "Left the waitlist.");
          router.refresh();
        })
        .catch((err) => {
          const message =
            err instanceof Error
              ? err.message
              : "Failed to leave the waitlist.";
          captureClientEvent(ANALYTICS_EVENTS.EVENT_WAITLIST_LEAVE_FAILED, {
            hiiveSlug,
            eventSlug,
            occurrencePassId,
            reason: message,
          });
          setError(message);
          toast.error(message);
        });
    });
  };

  const renderPassList = (occurrence: NormalizedOccurrence) => {
    const visiblePasses = occurrence.passes.filter(
      (pass) =>
        (pass.isActive || pass.waitlistState !== null) &&
        (!now ||
          ((!pass.saleStarts || pass.saleStarts <= now) &&
            (!pass.saleEnds || now < pass.saleEnds))),
    );

    if (visiblePasses.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-border/50 bg-surface/50 px-4 py-12 text-center">
          <Ticket className="mb-3 h-10 w-10 text-text-tertiary/50" />
          <p className="font-medium text-text-secondary">
            Ticket sales for this slot will open soon.
          </p>
          <p className="text-sm text-text-tertiary">
            Check back later for updates.
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-[26px] border border-border bg-surface shadow-sm dark:border-glass-border-10 dark:bg-[#1f1f21] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
        {visiblePasses.map((pass, passIndex) => {
          const selection = selectedPasses[pass.occurrencePassId];
          const quantity = selection?.quantity ?? 0;
          const seatsSelected = selection?.attendees.length ?? 0;
          const claimedHoldSeats =
            selection?.waitlistEntryId ||
            pass.waitlistState?.status === "offered"
              ? pass.attendeesPerPass
              : 0;
          const {
            soldOut: isSoldOut,
            canAdd: hasRoomToAdd,
            label: availabilityLabel,
          } = bookingPassAvailability({
            passRemaining: pass.remaining,
            occurrenceRemaining: occurrence.remaining,
            attendeesPerPass: pass.attendeesPerPass,
            selectedPassSeats: seatsSelected,
            selectedOccurrenceSeats: totalSelectedAttendees,
            claimedHoldSeats,
          });
          // A balanced ticket is split between men and women only; the note
          // under the row says why it can't be added. With their own side
          // full, a buyer can still add tickets for the other side.
          const othersCap = othersOnlyCap(pass);
          const canAdd =
            othersCap !== undefined
              ? (othersCap === null || seatsSelected < othersCap) &&
                (occurrence.remaining === null ||
                  totalSelectedAttendees < occurrence.remaining)
              : hasRoomToAdd && pass.balance?.viewerSide !== "other";
          const otherSideLabel =
            pass.balance?.viewerSide === "male" ? "women" : "men";
          const selfOnlyPass = isSelfOnlyPass(pass);
          const hasActiveWaitlist = pass.waitlistState?.status === "active";
          const hasOfferedWaitlist = pass.waitlistState?.status === "offered";
          const controlWidthClass = "h-10 w-[6.4rem] sm:h-11 sm:w-[8rem]";
          const priceLabel =
            pass.priceAmountMinor === 0 ? "Free" : pass.priceLabel;
          const settlementPriceLabel =
            pass.priceAmountMinor === 0
              ? null
              : settlementPreviewLabelFor(pass.priceAmountMinor, pass.currency);
          const settlementStandardPriceLabel = settlementPreviewLabelFor(
            pass.standardPriceAmountMinor,
            pass.currency,
          );
          const hasMemberPrice =
            pass.tierPriceAmountMinor != null &&
            pass.priceAmountMinor < pass.standardPriceAmountMinor;
          const isDepositPass = pass.isDeposit && pass.totalPriceMinor != null;
          const depositBreakdownLabel = isDepositPass
            ? `${settlementPriceLabel ?? priceLabel} now · ${
                settlementPreviewLabelFor(
                  pass.totalPriceMinor ?? 0,
                  pass.currency,
                ) ?? formatCurrency(pass.totalPriceMinor ?? 0, pass.currency)
              } total`
            : null;

          /*
           * Whether a pack reaches this exact pass, and whose.
           *
           * Per row rather than per event, because a pack scoped to the drop-in
           * does not cover the single-occupancy cabin beside it, and a badge on
           * both would send somebody to a checkout that refuses them. A deposit
           * pass can never be covered (B17), so it is never marked.
           */
          const packLine = {
            eventId: event.id,
            eventPassTypeId: pass.passTypeId,
          };
          const ownedPack =
            pass.paymentCollection === "at_venue" || pass.isDeposit
              ? null
              : holdingCovering(viewerPackHoldings, packLine);
          const sellablePack =
            pass.paymentCollection === "at_venue" || pass.isDeposit
              ? null
              : packEligibilityFor(packs, {
                  ...packLine,
                  listPriceMinor: pass.priceAmountMinor,
                });

          return (
            <div
              key={pass.occurrencePassId}
              id={`booking-pass-${pass.occurrencePassId}`}
            >
              {passIndex > 0 && (
                <div className="mx-4 border-t border-border sm:mx-8" />
              )}
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 px-4 py-5 sm:px-8 sm:py-6">
                <div className="min-w-0 space-y-1.5">
                  <div className="space-y-1">
                    <h3 className="text-[0.96rem] leading-[1.18] font-semibold tracking-[-0.015em] text-pretty text-text-primary sm:text-[1.16rem]">
                      {pass.name}
                    </h3>
                    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.92rem] leading-none font-medium text-text-secondary sm:text-[1rem]">
                      <span>{settlementPriceLabel ?? priceLabel}</span>
                      {isDepositPass ? (
                        <span className="rounded-full bg-[#F5E6B8]/12 px-2 py-0.5 text-[0.64rem] font-semibold tracking-[0.08em] text-[#F5E6B8] uppercase">
                          Deposit
                        </span>
                      ) : null}
                      {ownedPack ? (
                        <PackCoveredBadge covered="owned" />
                      ) : sellablePack ? (
                        <PackCoveredBadge covered="available" />
                      ) : null}
                      {settlementPriceLabel ? (
                        <span className="text-xs text-text-tertiary sm:text-sm">
                          Listed as {priceLabel}
                        </span>
                      ) : null}
                      {hasMemberPrice ? (
                        <>
                          <span className="text-xs text-text-tertiary line-through sm:text-sm">
                            {settlementStandardPriceLabel ??
                              pass.standardPriceLabel}
                          </span>
                          <span className="rounded-full bg-[#F5E6B8]/12 px-2 py-0.5 text-[0.64rem] font-semibold tracking-[0.08em] text-[#F5E6B8] uppercase">
                            Member price
                          </span>
                        </>
                      ) : null}
                    </p>
                    {depositBreakdownLabel ? (
                      <p className="text-xs text-text-tertiary sm:text-sm">
                        {depositBreakdownLabel}
                      </p>
                    ) : null}
                  </div>

                  {pass.paymentCollection === "at_venue" && (
                    <p className="text-sm font-medium">
                      Pay at venue · Reserve here, pay the full amount at entry.
                    </p>
                  )}
                  {pass.description ? (
                    <div className="max-w-xl text-[0.72rem] leading-relaxed text-text-secondary/80 sm:text-[0.8rem]">
                      {pass.description}
                    </div>
                  ) : null}

                  {/* "0 left" is wrong for a balanced ticket: it's this
                      side that's waiting, not the room that's full. */}
                  {availabilityLabel && !(pass.balance && isSoldOut) ? (
                    <p className="text-[0.69rem] text-text-tertiary sm:text-[0.76rem]">
                      {availabilityLabel}
                    </p>
                  ) : null}
                </div>

                <div className="justify-self-end">
                  {quantity > 0 && !selfOnlyPass ? (
                    <div
                      className={cn(
                        controlWidthClass,
                        "grid grid-cols-[1fr_auto_1fr] items-center overflow-hidden rounded-[22px] bg-[#f4f1ee] text-black",
                      )}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-full w-full rounded-none text-black hover:bg-black/5 disabled:opacity-30 dark:text-black dark:hover:bg-black/5"
                        onClick={() =>
                          handleAdjustQuantity(
                            pass.occurrencePassId,
                            "decrease",
                          )
                        }
                        disabled={isPending || quantity === 0}
                      >
                        <Minus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </Button>
                      <span className="min-w-[2rem] text-center text-[1.02rem] font-semibold text-black tabular-nums sm:min-w-[2.3rem] sm:text-[1.18rem]">
                        {quantity}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-full w-full rounded-none text-black hover:bg-black/5 disabled:opacity-30 dark:text-black dark:hover:bg-black/5"
                        onClick={() =>
                          handleAdjustQuantity(
                            pass.occurrencePassId,
                            "increase",
                          )
                        }
                        disabled={
                          isPending ||
                          isWaitlistPending ||
                          !canInteractWithPasses ||
                          !canAdd ||
                          selfOnlyPass ||
                          (hasOfferedWaitlist && quantity >= 1)
                        }
                      >
                        <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  ) : quantity > 0 && selfOnlyPass ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        controlWidthClass,
                        "rounded-[16px] border-emerald-400/35 bg-emerald-500/16 text-[0.8rem] font-semibold text-emerald-100 hover:bg-emerald-500/22 disabled:opacity-100 sm:text-[0.88rem]",
                      )}
                      onClick={() =>
                        handleAdjustQuantity(pass.occurrencePassId, "decrease")
                      }
                      disabled={isPending}
                    >
                      Claimed
                    </Button>
                  ) : pass.requiresApproval &&
                    (pass.joinRequest?.status !== "approved" ||
                      // Approved with a live hold: it's charged when a spot
                      // opens, so a second checkout is never offered.
                      pass.joinRequest.hold === "held" ||
                      pass.joinRequest.hold === "charged") ? (
                    <Button
                      asChild={pass.joinRequest?.status !== "declined"}
                      variant="outline"
                      size="sm"
                      className={cn(
                        // Grows past the stepper's width: "Request to join"
                        // is the whole call to action here.
                        "h-10 min-w-[6.4rem] px-3.5 sm:h-11 sm:min-w-[8rem]",
                        "rounded-[16px] border-border bg-transparent text-[0.78rem] font-semibold text-text-primary hover:bg-surface-alt disabled:opacity-50 sm:text-[0.86rem] dark:border-white/60 dark:hover:bg-glass-bg-04",
                      )}
                      disabled={pass.joinRequest?.status === "declined"}
                    >
                      {pass.joinRequest?.status === "declined" ? (
                        "Not this time"
                      ) : (
                        <Link
                          href={`/hiives/${hiive.slug}/events/${eventSlug}/request?ticket=${pass.passTypeId}`}
                        >
                          {pass.joinRequest?.status === "approved"
                            ? "Approved"
                            : pass.joinRequest?.status === "pending"
                              ? // A request whose card isn't held never
                                // reached the host: it isn't "requested".
                                pass.joinRequest.hold === "unpaid"
                                ? "Finish request"
                                : "Requested"
                              : "Request to join"}
                        </Link>
                      )}
                    </Button>
                  ) : hasOfferedWaitlist ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        controlWidthClass,
                        "rounded-[16px] border-border bg-transparent text-[0.78rem] font-semibold text-text-primary hover:bg-surface-alt disabled:opacity-50 sm:text-[0.86rem] dark:border-white/60 dark:hover:bg-glass-bg-04",
                      )}
                      onClick={() =>
                        handleAdjustQuantity(pass.occurrencePassId, "increase")
                      }
                      disabled={
                        isPending || isWaitlistPending || !canInteractWithPasses
                      }
                    >
                      Claim offer
                    </Button>
                  ) : hasActiveWaitlist && isSoldOut ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        controlWidthClass,
                        "rounded-[16px] border-border bg-transparent text-[0.74rem] font-semibold text-text-secondary hover:bg-surface-alt hover:text-text-primary disabled:opacity-50 sm:text-[0.8rem] dark:border-white/35 dark:hover:bg-glass-bg-04",
                      )}
                      onClick={() => handleLeaveWaitlist(pass.occurrencePassId)}
                      disabled={isPending || isWaitlistPending}
                    >
                      Leave waitlist
                    </Button>
                  ) : isSoldOut && pass.waitlistEnabled ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        controlWidthClass,
                        "rounded-[16px] border-border bg-transparent text-[0.78rem] font-semibold text-text-primary hover:bg-surface-alt disabled:opacity-50 sm:text-[0.84rem] dark:border-white/60 dark:hover:bg-glass-bg-04",
                      )}
                      onClick={() => handleJoinWaitlist(pass.occurrencePassId)}
                      disabled={
                        isPending || isWaitlistPending || !canInteractWithPasses
                      }
                    >
                      Join waitlist
                    </Button>
                  ) : pass.inviteOnly && canAdd ? (
                    <GuestListClaimButton
                      eligible={pass.guestListEligible}
                      onClaim={() => {
                        if (!sessionUser) openBookingAuthPrompt();
                        else
                          handleAdjustQuantity(
                            pass.occurrencePassId,
                            "increase",
                          );
                      }}
                      disabled={
                        isPending || isWaitlistPending || !canInteractWithPasses
                      }
                      className={cn(
                        controlWidthClass,
                        "rounded-[16px] border-border bg-transparent text-[0.84rem] font-semibold text-text-primary hover:bg-surface-alt disabled:opacity-50 sm:text-[0.92rem] dark:border-white/60",
                      )}
                    />
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        controlWidthClass,
                        "rounded-[16px] border-border bg-transparent text-[0.84rem] font-semibold text-text-primary hover:bg-surface-alt disabled:opacity-50 sm:text-[0.92rem] dark:border-white/60 dark:hover:bg-glass-bg-04",
                      )}
                      onClick={() =>
                        handleAdjustQuantity(pass.occurrencePassId, "increase")
                      }
                      disabled={
                        isPending ||
                        isWaitlistPending ||
                        !canInteractWithPasses ||
                        !canAdd
                      }
                    >
                      {isSoldOut
                        ? "Sold out"
                        : !canAdd
                          ? pass.balance?.viewerSide === "other"
                            ? "Unavailable"
                            : "Limit reached"
                          : selfOnlyPass
                            ? "Claim"
                            : "Add"}
                    </Button>
                  )}
                </div>
                {pass.inviteOnly ? (
                  <p className="col-span-2 text-xs text-text-secondary">
                    {guestListTicketMessage(pass.guestListEligible)}
                  </p>
                ) : null}
                {pass.requiresApproval ? (
                  <p className="col-span-2 text-xs text-text-secondary">
                    {joinRequestNote(
                      pass.joinRequest ?? null,
                      priceLabel,
                      pass.cardHold ?? false,
                    )}
                  </p>
                ) : null}
                {pass.balance &&
                !hasActiveWaitlist &&
                !(othersCap !== undefined && quantity > 0) ? (
                  <BalancedTicketNote balance={pass.balance} />
                ) : null}
                {othersCap !== undefined && quantity === 0 ? (
                  <div className="col-span-2 -mt-1 pl-5.5">
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs font-semibold text-brand dark:text-[#b89cff]"
                      onClick={() =>
                        handleAdjustQuantity(pass.occurrencePassId, "increase")
                      }
                      disabled={
                        isPending || isWaitlistPending || !canInteractWithPasses
                      }
                    >
                      Book for {otherSideLabel} instead
                    </Button>
                  </div>
                ) : null}
                {othersCap !== undefined && quantity > 0 ? (
                  <p className="col-span-2 text-xs text-text-secondary">
                    These tickets are for {otherSideLabel} only. You're not on
                    one, since spots for{" "}
                    {otherSideLabel === "women" ? "men" : "women"} are taken.
                  </p>
                ) : null}
                {pass.waitlistHeadStartUntil && !pass.waitlistState ? (
                  <WaitlistHeadStartNote until={pass.waitlistHeadStartUntil} />
                ) : null}
                {hasActiveWaitlist ? (
                  <WaitlistPlaceNote
                    position={pass.waitlistState?.position ?? null}
                    mode={pass.waitlistMode ?? "notify"}
                    side={pass.balance?.viewerSide ?? null}
                    spotOpen={!isSoldOut}
                    onLeave={
                      isSoldOut
                        ? undefined
                        : () => handleLeaveWaitlist(pass.occurrencePassId)
                    }
                    leaving={isWaitlistPending}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const currencyForTotal = selectedPassEntries.length
    ? (passInfoMap.get(Number(selectedPassEntries[0][0]))?.pass.currency ??
      "INR")
    : "INR";
  const totalSettlementPreviewMinor = settlementPreviewFor(
    totalAmountMinor,
    currencyForTotal,
  );
  const selectedDiscountSettlementPreviewMinor = settlementPreviewFor(
    selectedDiscountMinor,
    currencyForTotal,
  );
  const totalDisplay =
    totalSettlementPreviewMinor != null
      ? formatCurrency(totalSettlementPreviewMinor, SETTLEMENT_CURRENCY)
      : formatCurrency(totalAmountMinor, currencyForTotal);
  const totalListedDisplay = formatCurrency(totalAmountMinor, currencyForTotal);
  const attendeeInfoItems = [
    "Pick who each ticket is for before checkout so entry is issued to the right person.",
    "Free tickets stay with your booking and can't be reassigned to someone else.",
    requiresEligibilityChecks
      ? "Only people who are eligible for this event can be assigned to these tickets."
      : "The same person can't be assigned to more than one ticket in the same booking.",
  ];

  /**
   * Order summary and CTA, rendered in two places.
   *
   * At mobile widths it lives in the fixed bottom bar, which is the pattern the
   * flow converts on. At `lg` and up that bar becomes a sticky right rail
   * instead: a full-width footer on a 1440px screen puts the price at the far
   * left and Checkout at the far right with 900px of nothing between them, which
   * reads as two unrelated controls.
   *
   * Defined as values rather than a component so the closure over pricing state
   * stays exactly as it was — extracting a component here would mean threading a
   * dozen props for no benefit.
   */
  const orderSummaryDetails = (
    <div>
      <p className="text-sm text-text-secondary dark:text-white/58">
        {totalPassesSelected} ticket
        {totalPassesSelected === 1 ? "" : "s"}
      </p>
      <p className="text-xl font-bold text-text-primary dark:text-white">
        {packCoversBooking ? "No payment due" : totalDisplay}
      </p>
      {packCoversBooking ? (
        <p className="mt-0.5 max-w-[16rem] text-xs text-success dark:text-[#F5E6B8]">
          Covered by your {packPreview?.option?.packName}. Uses{" "}
          {packPreview?.option?.credits} credit
          {packPreview?.option?.credits === 1 ? "" : "s"}.
        </p>
      ) : null}
      {!packCoversBooking && totalSettlementPreviewMinor != null ? (
        <p className="mt-0.5 text-xs text-text-tertiary dark:text-white/45">
          Listed as {totalListedDisplay}. Final INR is set at booking.
        </p>
      ) : null}
      {!packCoversBooking && selectedDiscountMinor > 0 ? (
        <p className="mt-0.5 text-xs text-success dark:text-[#F5E6B8]">
          Member price applied. Saved{" "}
          {selectedDiscountSettlementPreviewMinor != null
            ? formatCurrency(
                selectedDiscountSettlementPreviewMinor,
                SETTLEMENT_CURRENCY,
              )
            : formatCurrency(selectedDiscountMinor, currencyForTotal)}
        </p>
      ) : null}
      {hasDepositSelection ? (
        <p className="mt-0.5 max-w-[16rem] text-xs text-text-tertiary dark:text-white/45">
          You&apos;re paying a deposit today. The remaining balance is settled
          directly with the organizer.
        </p>
      ) : null}
    </div>
  );

  const checkoutButton = (
    <Button
      type="button"
      size="lg"
      variant="solid"
      className="rounded-full px-8 font-semibold"
      disabled={isSubmitDisabled}
      onClick={handleSubmit}
    >
      {isPending
        ? "Processing..."
        : packCoversBooking
          ? "Confirm booking"
          : "Checkout"}
    </Button>
  );

  return (
    <div className="min-h-dvh bg-background">
      {/* Hand-off cover. Between the gate and the payment page the viewer would
          otherwise watch their basket reappear item by item and then vanish
          into a redirect. Above the sticky header (z-1000) and the checkout
          footer so nothing pokes through. */}
      {handoffInFlight ? (
        <div
          aria-live="polite"
          role="status"
          className="fixed inset-0 z-2000 flex flex-col items-center justify-center gap-4 bg-background px-8 text-center"
        >
          <Spinner className="size-8 text-brand" />
          <div>
            <p className="text-lg font-semibold text-text-primary">
              Finishing your booking
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              Holding your tickets — this only takes a moment.
            </p>
          </div>
        </div>
      ) : null}
      <div className="flex min-h-dvh flex-col">
        {/* Sticky Header */}
        <header className="status-corner-header sticky top-0 z-1000 border-b border-border bg-background/90 pt-[env(safe-area-inset-top)] backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
          <div className="mx-auto flex h-16 w-full max-w-4xl items-center gap-4 px-4 lg:max-w-6xl">
            <BackButton
              fallbackHref={`/hiives/${hiiveSlug}/events/${eventSlug}`}
              label="Back to event"
              className="z-1000 h-10 w-10 rounded-full border border-border bg-surface hover:bg-surface-alt"
              iconClassName="text-text-primary dark:text-white"
            />
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-lg font-semibold text-text-primary dark:text-white">
                {event.name}
              </h1>
              <p className="truncate text-xs text-text-secondary dark:text-white/60">
                {event.pattern === "single" &&
                normalizedOccurrences[0]?.start &&
                normalizedOccurrences[0]?.end
                  ? (() => {
                      try {
                        const occurrence =
                          selectedOccurrence ?? normalizedOccurrences[0];
                        if (!occurrence?.start || !occurrence?.end)
                          return `Hosted by ${hiive.name}`;
                        const timeZone = resolveEventTimeZone(
                          occurrence.timezone,
                        );
                        const venue = event.venueName
                          ? ` | ${event.venueName}`
                          : "";
                        return isSameEventDay(
                          occurrence.start,
                          occurrence.end,
                          timeZone,
                        )
                          ? `${formatEventDate(occurrence.start, timeZone)} | ${formatEventTimeRange(occurrence.start, occurrence.end, timeZone)}${venue}`
                          : `${formatEventDate(occurrence.start, timeZone)}, ${formatEventTime(occurrence.start, timeZone)} \u2013 ${formatEventDate(occurrence.end, timeZone)}, ${formatEventTime(occurrence.end, timeZone)}${venue}`;
                      } catch {
                        return `Hosted by ${hiive.name}`;
                      }
                    })()
                  : `Hosted by ${hiive.name}`}
              </p>
            </div>
          </div>
        </header>

        {/*
          Two columns from `lg` up. Below that this is one phone-width column
          with a fixed bottom bar, which is the flow that currently converts and
          is deliberately untouched. `pb-64` exists only to clear that bar, so it
          collapses once the bar is gone.
        */}
        <main className="mx-auto w-full max-w-4xl px-4 pt-8 pb-64 sm:pt-10 sm:pb-64 lg:max-w-6xl lg:pb-16">
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-8">
            <div className="space-y-6">
              {/* Alerts / Restrictions */}
              {/* A guest is only shown a wall when signing in would not clear it.
                "Sign in to book this event." is not a wall any more — it is
                answered by the Checkout button, and leading with a warning
                banner is exactly the drop-off this flow was built to remove. */}
              {bookingRestriction &&
              !canBookEvent &&
              !guestSignInIsTheOnlyBlock ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                  <div className="flex gap-3">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      <p className="font-medium">{bookingRestriction}</p>
                      {!sessionUser && (
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-3 border-amber-900/30 bg-transparent text-amber-950 hover:bg-amber-100"
                          onClick={openBookingAuthPrompt}
                        >
                          Sign in to continue
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {allowedTierNames.length > 0 ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                  <p className="text-sm font-medium">Tier-restricted event</p>
                  <p className="mt-1 text-sm opacity-90">
                    Only members of these tiers can join:{" "}
                    {allowedTierNames.join(", ")}
                  </p>
                </div>
              ) : null}

              {dateKeys.length === 1 && normalizedOccurrences[0] ? (
                <div className="flex min-h-15 w-fit items-center gap-3 rounded-2xl border border-brand bg-brand px-5 py-3 text-brand-foreground shadow-sm dark:border-white dark:bg-white dark:text-black">
                  <CalendarDays className="size-5 shrink-0 opacity-70" />
                  <div>
                    <p className="font-semibold">
                      {formatEventDate(
                        normalizedOccurrences[0].start,
                        resolveEventTimeZone(normalizedOccurrences[0].timezone),
                      )}
                    </p>
                    {!hasSessionChoice ? (
                      <p className="text-sm opacity-70">
                        {formatEventTimeRange(
                          normalizedOccurrences[0].start,
                          normalizedOccurrences[0].end,
                          resolveEventTimeZone(
                            normalizedOccurrences[0].timezone,
                          ),
                        )}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {/* Date & Time Selection — hidden only when there is nothing
                  to choose: a one-time event with a single session */}
              {hasSessionChoice && (
                <DateTimeSlotPicker
                  appearance="solid"
                  showDates={dateKeys.length > 1}
                  title={
                    dateKeys.length === 1
                      ? "Choose a time"
                      : "Choose date and time"
                  }
                  dateOptions={dateKeys.map((dateKey) => {
                    const occurrencesOnDate =
                      occurrencesByDate.get(dateKey) ?? [];
                    const disabledReason = getDateUnavailableReason(
                      occurrencesOnDate,
                      now,
                    );
                    return {
                      key: dateKey,
                      date: dateFromKey(dateKey),
                      disabled: Boolean(disabledReason),
                      disabledReason,
                    };
                  })}
                  selectedDateKey={selectedDateKey}
                  onDateChange={(dateKey) => {
                    const occurrencesOnDate =
                      occurrencesByDate.get(dateKey) ?? [];
                    const firstOccurrence =
                      occurrencesOnDate.find(
                        (occurrence) =>
                          !getOccurrenceUnavailableReason(occurrence, now),
                      ) ?? occurrencesOnDate[0];
                    setSelectedDateKey(dateKey);
                    setSelectedOccurrenceId(firstOccurrence?.id ?? null);
                    setSelectedPasses({});
                    setError(null);
                  }}
                  onDisabledDateClick={(option) => {
                    toast.error(
                      option.disabledReason ??
                        "No sessions on this date can be booked.",
                    );
                  }}
                  slotOptions={occurrencesForSelectedDate
                    .filter(
                      (occurrence) =>
                        occurrence?.start != null &&
                        occurrence?.end != null &&
                        !Number.isNaN(occurrence.start.valueOf()) &&
                        !Number.isNaN(occurrence.end.valueOf()),
                    )
                    .map((occurrence) => {
                      const timeZone = resolveEventTimeZone(
                        occurrence.timezone,
                      );
                      let timeRange: string;
                      let label: string;
                      try {
                        timeRange = formatEventTimeRange(
                          occurrence.start,
                          occurrence.end,
                          timeZone,
                        );
                        label = timeRange;
                      } catch {
                        timeRange = occurrence.slotLabel ?? "Slot";
                        label = occurrence.slotLabel ?? "Slot";
                      }
                      const disabledReason = getOccurrenceUnavailableReason(
                        occurrence,
                        now,
                      );
                      return {
                        key: occurrence.id.toString(),
                        label,
                        description: disabledReason
                          ? disabledReason.includes("past")
                            ? "Past"
                            : "Unavailable"
                          : occurrence.slotLabel,
                        disabled: Boolean(disabledReason),
                        disabledReason,
                        ariaLabel: occurrence.slotLabel
                          ? `${occurrence.slotLabel}, ${timeRange}`
                          : timeRange,
                      };
                    })}
                  selectedSlotKey={selectedOccurrenceId?.toString() ?? null}
                  onSlotChange={(slotKey) => {
                    setSelectedOccurrenceId(Number(slotKey));
                    setSelectedPasses({});
                    setError(null);
                  }}
                  onDisabledSlotClick={(option) => {
                    toast.error(
                      option.disabledReason ??
                        "This session can't be booked right now.",
                    );
                  }}
                  emptyLabel="No upcoming dates available."
                  selectedDateEmptyLabel="No slots on this date."
                />
              )}

              {/* Tickets Section */}
              <section className="space-y-4">
                <h2 className="text-[1.4rem] font-semibold text-text-primary sm:text-[1.55rem]">
                  Choose tickets
                </h2>

                {/* Above the prices, because it changes what they mean.
                    Somebody holding a pack should not read four rupee figures
                    and reach for a card; somebody holding none is looking at a
                    price right now, which is the only moment "ten of these
                    costs less" is worth reading. */}
                {packs.length > 0 && eventPackEligibility ? (
                  <PackEligibilityNote
                    hiiveSlug={hiiveSlug}
                    holding={eventPackHolding}
                    percent={eventPackPercent}
                  />
                ) : null}

                {selectedOccurrence ? (
                  <>
                    {selectedOccurrenceUnavailableReason ? (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                        <div className="flex gap-3">
                          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                          <p className="text-sm font-medium">
                            {selectedOccurrenceUnavailableReason}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    {renderPassList(selectedOccurrence)}
                    {/* A waitlist spot is worth nothing if it arrives late —
                        the offer expires. This is the one place in the booking
                        flow where the notification IS the product, so the ask
                        sits directly under the row that created the wait. */}
                    {selectedOccurrence.passes.some(
                      (pass) => pass.waitlistState?.status === "active",
                    ) ? (
                      <PushNudge
                        surface="event_waitlist_joined"
                        params={{
                          eventName: event.name,
                          waitlistPosition:
                            selectedOccurrence.passes.find(
                              (pass) => pass.waitlistState?.status === "active",
                            )?.waitlistState?.position ?? undefined,
                        }}
                        confirmationScopes={[
                          "waitlist spots",
                          "changes to events you've booked",
                        ]}
                        className="mt-3"
                      />
                    ) : null}
                    {selectedOccurrence.passes.some(
                      (pass) => pass.waitlistState?.status === "active",
                    ) ? (
                      <WaitlistAppCard />
                    ) : null}
                  </>
                ) : (
                  <div className="rounded-[26px] border border-dashed border-border bg-surface px-4 py-8 text-center text-text-secondary sm:px-8">
                    {event.pattern === "single"
                      ? "No upcoming dates available for this event."
                      : "Select a date to view available tickets."}
                  </div>
                )}
              </section>

              {/* How you're paying. Sits after the tickets and before the
                  attendees because the pack decides whether there is anything
                  to pay at all, and the member should see that the moment the
                  basket has a price rather than at the checkout button. */}
              {selectedOccurrence && selectedPassEntries.length > 0 ? (
                <PackTenderSelector
                  preview={packPreview}
                  selectedHoldingId={packHoldingId}
                  onSelect={setPackHoldingId}
                  currency={currencyForTotal}
                  payableMinor={totalAmountMinor}
                  disabled={isPending}
                />
              ) : null}

              {/* Attendees Section */}
              {hasSeatBearingSelection && (
                <section ref={attendeeSectionRef} className="space-y-4 pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-[1.4rem] font-semibold text-text-primary sm:text-[1.55rem]">
                          Assign attendees
                        </h2>
                        <span className="rounded-full border border-border bg-surface px-3 py-1 text-[11px] font-medium tracking-[0.18em] text-text-secondary uppercase">
                          {totalSelectedAttendees} seat
                          {totalSelectedAttendees === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 shrink-0 rounded-full border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:text-text-primary"
                      onClick={() => setIsAttendeeInfoOpen(true)}
                    >
                      <CircleHelp className="h-4 w-4" />
                    </Button>
                  </div>

                  {/*
                    One card per seat, and nothing wrapping them. The pass name
                    is a plain caption above its seats rather than another card
                    header, so a ticket row is never a card inside a card.
                  */}
                  <div className="space-y-7">
                    {selectedOccurrence?.passes
                      .filter((pass) => {
                        if (!selectedPasses[pass.occurrencePassId])
                          return false;
                        return pass.attendeesPerPass > 0;
                      })
                      .map((pass) => {
                        const selection = selectedPasses[pass.occurrencePassId];
                        if (!selection) return null;
                        const selfOnlyPass = isSelfOnlyPass(pass);

                        return (
                          <div
                            key={pass.occurrencePassId}
                            className="space-y-3"
                          >
                            <div className="flex flex-wrap items-center gap-2 px-1 text-text-primary">
                              <Ticket className="h-4 w-4 text-text-secondary" />
                              <span className="font-medium">{pass.name}</span>
                              <span className="rounded-full border border-border bg-background-alt px-2.5 py-1 text-[11px] font-medium tracking-[0.18em] text-text-secondary uppercase">
                                {selection.quantity} selected
                              </span>
                              {selfOnlyPass ? (
                                <span className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium tracking-[0.18em] text-emerald-200 uppercase">
                                  Self only
                                </span>
                              ) : null}
                            </div>

                            <div className="space-y-3">
                              {selection.attendees.map((attendee, index) => {
                                const isCurrentUserSeat = Boolean(
                                  sessionUser?.id &&
                                    attendee.userId === sessionUser.id,
                                );
                                const isSelfSeat =
                                  selfOnlyPass && isCurrentUserSeat;
                                const isTagged = Boolean(attendee.userId);
                                const isGuest = isGuestAttendee(attendee);
                                const isAssigned =
                                  isTagged || isSelfSeat || isGuest;
                                const selfInBasket = Boolean(
                                  sessionUser?.id &&
                                    taggedUserIds.has(sessionUser.id),
                                );
                                const selfAlreadyAssigned =
                                  selfInBasket ||
                                  viewerHoldsTicketForSelectedOccurrence;
                                const isAssigningThisSeat =
                                  assigningSelfSeat ===
                                  selfSeatKey(pass.occurrencePassId, index);

                                return (
                                  <div
                                    key={
                                      attendee.seatKey ??
                                      `${pass.occurrencePassId}-${attendee.userId ?? "seat"}`
                                    }
                                    className={cn(
                                      "overflow-hidden rounded-[26px] border px-4 py-4 shadow-sm sm:px-5 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]",
                                      isAssigned
                                        ? "border-brand/25 bg-background-alt/55 dark:border-white/[0.14]"
                                        : "border-border bg-surface dark:border-glass-border-10 dark:bg-[#1f1f21]",
                                      isSelfSeat && "bg-emerald-500/8",
                                    )}
                                  >
                                    {/* Capped so an avatar + name doesn't sit alone in 700px of
                                            empty row once the column widens at lg. */}
                                    <div className="min-w-0 lg:max-w-2xl">
                                      <div className="mb-3 flex items-start justify-between gap-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="inline-flex min-h-7 items-center justify-center rounded-full border border-border bg-surface px-3 py-1 text-center text-[11px] font-semibold tracking-[0.16em] whitespace-nowrap text-text-primary uppercase dark:border-white/[0.12] dark:bg-white/[0.05]">
                                            Ticket {index + 1}
                                          </span>
                                          {isAssigned ? (
                                            <span className="inline-flex min-h-7 items-center justify-center rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-center text-[11px] font-semibold tracking-[0.16em] whitespace-nowrap text-emerald-700 uppercase dark:text-emerald-200">
                                              {isCurrentUserSeat
                                                ? "Assigned to you"
                                                : "Assigned"}
                                            </span>
                                          ) : null}
                                        </div>

                                        {isAssigned && !selfOnlyPass ? (
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 rounded-full border border-destructive/25 bg-destructive/[0.08] px-3 font-medium text-destructive hover:border-destructive/40 hover:bg-destructive/15 hover:text-destructive dark:border-destructive/30 dark:bg-destructive/15 dark:text-destructive dark:hover:bg-destructive/25 dark:hover:text-destructive"
                                            onClick={() =>
                                              handleClearTaggedAttendee(
                                                pass.occurrencePassId,
                                                index,
                                              )
                                            }
                                          >
                                            <X className="mr-1.5 h-3.5 w-3.5" />
                                            Remove
                                          </Button>
                                        ) : null}
                                      </div>

                                      {isSelfSeat ? (
                                        <div className="flex min-w-0 items-center gap-3">
                                          <Avatar className="h-10 w-10 border border-border">
                                            <AvatarImage
                                              src={
                                                sessionUser?.image ?? undefined
                                              }
                                            />
                                            <AvatarFallback>
                                              {getInitials(
                                                sessionUser?.name ?? "Me",
                                              )}
                                            </AvatarFallback>
                                          </Avatar>
                                          <div className="min-w-0 flex-1">
                                            <p className="truncate font-medium text-text-primary">
                                              {sessionUser?.name ?? "You"}
                                            </p>
                                            <p className="text-sm text-emerald-200/85">
                                              Complimentary tickets can only be
                                              claimed for yourself.
                                            </p>
                                          </div>
                                        </div>
                                      ) : isTagged ? (
                                        <div className="flex min-w-0 items-center gap-3">
                                          <Avatar className="h-10 w-10 border border-border">
                                            <AvatarImage
                                              src={
                                                attendee.avatarUrl ?? undefined
                                              }
                                            />
                                            <AvatarFallback>
                                              {getInitials(
                                                firstRealDisplayName(
                                                  attendee.displayName,
                                                ) ?? "Attendee",
                                              )}
                                            </AvatarFallback>
                                          </Avatar>
                                          <div className="min-w-0 flex-1">
                                            <p className="truncate font-medium text-text-primary">
                                              {firstRealDisplayName(
                                                attendee.displayName,
                                              ) ?? "Attendee"}
                                            </p>
                                            <p className="text-sm text-text-secondary">
                                              {isCurrentUserSeat
                                                ? "This is your ticket"
                                                : [
                                                    attendee.memberSide ===
                                                    "female"
                                                      ? "Woman"
                                                      : attendee.memberSide ===
                                                          "male"
                                                        ? "Man"
                                                        : null,
                                                    attendee.handle
                                                      ? `@${attendee.handle}`
                                                      : null,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" · ") ||
                                                  "Name not set"}
                                            </p>
                                          </div>
                                        </div>
                                      ) : isGuest ? (
                                        /* Mirrors the tagged chip exactly, with
                                             the email where the handle goes, so a
                                             guest seat reads as equally settled
                                             rather than as a lesser state. */
                                        <div className="flex min-w-0 items-center gap-3">
                                          <Avatar className="h-10 w-10 border border-border">
                                            <AvatarFallback>
                                              {getInitials(
                                                attendee.guestName ?? "Guest",
                                              )}
                                            </AvatarFallback>
                                          </Avatar>
                                          <div className="min-w-0 flex-1">
                                            <p className="truncate font-medium text-text-primary">
                                              {attendee.guestName ?? "Guest"}
                                            </p>
                                            <p className="truncate text-sm text-text-secondary">
                                              {attendee.guestGender
                                                ? `${attendee.guestGender === "female" ? "Woman" : "Man"} · `
                                                : null}
                                              {attendee.guestEmail}
                                            </p>
                                            <p className="mt-1 text-xs text-text-tertiary">
                                              We'll email their ticket once you
                                              pay.
                                            </p>
                                            {selectedOccurrence ? (
                                              <GuestSeatShare
                                                guestName={
                                                  attendee.guestName ?? "Guest"
                                                }
                                                guestEmail={
                                                  attendee.guestEmail ?? ""
                                                }
                                                eventName={event.name}
                                                dateLabel={formatEventDate(
                                                  selectedOccurrence.start,
                                                  "EEE d MMM",
                                                )}
                                                eventPath={`/hiives/${hiiveSlug}/events/${eventSlug}`}
                                              />
                                            ) : null}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="space-y-3">
                                          {selfAlreadyAssigned ? (
                                            <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 dark:border-emerald-400/20 dark:bg-emerald-500/10">
                                              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                                                <Check
                                                  className="h-3.5 w-3.5"
                                                  aria-hidden="true"
                                                />
                                              </span>
                                              <div className="min-w-0">
                                                <p className="text-[13px] leading-tight font-semibold text-emerald-900 dark:text-emerald-100">
                                                  {selfInBasket
                                                    ? "You’re already on another ticket in this booking"
                                                    : "You already have a ticket for this date"}
                                                </p>
                                                <p className="mt-1 text-xs leading-relaxed text-emerald-800/80 dark:text-emerald-200/70">
                                                  Each person can only have one
                                                  ticket. Add their details
                                                  below to send this one to
                                                  them.
                                                </p>
                                              </div>
                                            </div>
                                          ) : othersOnlyCap(pass) !==
                                            undefined ? (
                                            // Their own side is full, so this
                                            // seat can only go to the other.
                                            <p className="text-sm text-text-secondary">
                                              Spots for{" "}
                                              {pass.balance?.viewerSide ===
                                              "male"
                                                ? "men are taken, so this ticket is for a woman."
                                                : "women are taken, so this ticket is for a man."}
                                            </p>
                                          ) : (
                                            <>
                                              <p className="text-sm text-text-secondary">
                                                {requiresEligibilityChecks
                                                  ? "Select an eligible attendee for this ticket."
                                                  : "Choose who this ticket is for."}
                                              </p>
                                              <div className="space-y-2.5">
                                                <Button
                                                  type="button"
                                                  variant="solid"
                                                  className="h-11 w-full min-w-0 rounded-full px-4 font-semibold"
                                                  onClick={() =>
                                                    void handleAssignSelf(
                                                      pass.occurrencePassId,
                                                      index,
                                                    )
                                                  }
                                                  disabled={
                                                    isPending ||
                                                    assigningSelfSeat !== null
                                                  }
                                                >
                                                  {isAssigningThisSeat ? (
                                                    <Spinner className="mr-2 h-4 w-4" />
                                                  ) : (
                                                    <UserPlus className="mr-2 h-4 w-4" />
                                                  )}
                                                  {isAssigningThisSeat
                                                    ? "Assigning…"
                                                    : "Assign to me"}
                                                </Button>
                                              </div>
                                            </>
                                          )}

                                          <div className="flex items-center gap-3">
                                            <span className="h-px flex-1 bg-border" />
                                            <span
                                              className={cn(
                                                "text-[10px] font-semibold tracking-[0.18em] uppercase",
                                                selfAlreadyAssigned
                                                  ? "text-text-primary dark:text-white"
                                                  : "text-text-tertiary",
                                              )}
                                            >
                                              {selfAlreadyAssigned
                                                ? "Send this ticket to someone else ↓"
                                                : othersOnlyCap(pass) !==
                                                    undefined
                                                  ? "Who's it for? ↓"
                                                  : "or send it to someone"}
                                            </span>
                                            <span className="h-px flex-1 bg-border" />
                                          </div>

                                          <GuestAttendeeForm
                                            seatLabel={`${pass.occurrencePassId}-ticket-${index + 1}`}
                                            askGender={Boolean(pass.balance)}
                                            fullSides={fullSidesForSeat(
                                              pass,
                                              index,
                                            )}
                                            disabled={
                                              !canInteractWithPasses ||
                                              selfOnlyPass ||
                                              pass.inviteOnly ||
                                              !guestAttendeesAllowed ||
                                              requiresEligibilityChecks
                                            }
                                            disabledReason={
                                              selfOnlyPass
                                                ? "Complimentary tickets can only be claimed for your own account."
                                                : pass.inviteOnly
                                                  ? "Invite-only tickets must be assigned to a Hiive account whose verified phone or email is on the guest list."
                                                  : requiresEligibilityChecks
                                                    ? "This event is limited to specific members, so every ticket must be assigned to a Hiive account."
                                                    : !guestAttendeesAllowed
                                                      ? "The organizer requires each ticket to be linked to a Hiive account. Choose an attendee above or assign the ticket to yourself."
                                                      : null
                                            }
                                            interceptSubmit={
                                              interceptAttendeeAssignment
                                            }
                                            onConfirm={(values) =>
                                              handleAssignGuest(
                                                pass.occurrencePassId,
                                                index,
                                                values,
                                              )
                                            }
                                          />

                                          <div className="flex justify-center pt-1">
                                            <AttendeeTagPicker
                                              hiiveId={hiive.id}
                                              allowedTierIds={
                                                event.allowedTierIds
                                              }
                                              allowedGenders={
                                                event.allowedGenders
                                              }
                                              disabled={!canInteractWithPasses}
                                              buttonLabel="Add someone already on Hiive"
                                              balanced={Boolean(pass.balance)}
                                              fullSides={fullSidesForSeat(
                                                pass,
                                                index,
                                              )}
                                              occurrencePassId={
                                                pass.occurrencePassId
                                              }
                                              invite={{
                                                eventName: event.name,
                                                eventPath: `/hiives/${hiiveSlug}/events/${eventSlug}`,
                                              }}
                                              selectedUserIds={taggedUserIds}
                                              currentUserId={attendee.userId}
                                              occurrenceId={
                                                selectedOccurrence?.id
                                              }
                                              interceptOpen={
                                                interceptAttendeeAssignment
                                              }
                                              className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-black/[0.08] bg-white/60 px-3.5 text-xs font-medium text-text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_14px_-6px_rgba(15,10,40,0.22)] backdrop-blur-xl backdrop-saturate-150 transition-[background-color,transform] duration-150 ease-out hover:bg-white/80 active:scale-[0.97] dark:border-white/[0.14] dark:bg-white/[0.08] dark:text-white/90 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_4px_14px_-6px_rgba(0,0,0,0.5)] dark:hover:bg-white/[0.14]"
                                              onSelect={(suggestion) =>
                                                handleSelectTaggedAttendee(
                                                  pass.occurrencePassId,
                                                  index,
                                                  suggestion,
                                                )
                                              }
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </section>
              )}
            </div>

            {/*
              The desktop rail. `hidden lg:block` rather than a responsive
              reflow of the fixed bar, because the two are genuinely different
              components: one is pinned to the viewport, the other scrolls with
              the page until it sticks under the header.
            */}
            {totalPassesSelected > 0 ? (
              <aside className="hidden lg:sticky lg:top-24 lg:block">
                <div className="space-y-4 rounded-[26px] border border-border bg-surface p-5 shadow-sm dark:border-glass-border-10 dark:bg-[#1f1f21]">
                  <h2 className="text-[11px] font-semibold tracking-[0.18em] text-text-tertiary uppercase">
                    Order summary
                  </h2>
                  {orderSummaryDetails}
                  <div className="space-y-2">
                    {error ? (
                      <p className="text-sm text-error">{error}</p>
                    ) : null}
                    <div className="[&>button]:w-full">{checkoutButton}</div>
                  </div>
                </div>
              </aside>
            ) : null}
          </div>
        </main>

        <DeferredAnswersDrawer
          open={answersDrawerOpen}
          onOpenChange={setAnswersDrawerOpen}
          answers={deferredProfileAnswers}
          allowedGenders={event.allowedGenders}
          onAnswered={() => {
            // Eager, because the resumed action runs on this same tick and
            // reads the ref. See `deferredAnswersSatisfiedRef`.
            deferredAnswersSatisfiedRef.current = true;
            const resume = deferredAnswersResumeRef.current;
            deferredAnswersResumeRef.current = null;
            resume?.();
          }}
          onRefused={() => {
            // The stored answer disqualifies them, so the page's own gating is
            // now wrong: it was rendered for somebody with no gender on file.
            // Refresh so the footer tells the same story as the drawer.
            router.refresh();
          }}
          context={{
            surface: "event",
            hiiveSlug,
            eventSlug,
            occurrenceId: selectedOccurrence?.id ?? null,
          }}
        />

        {bookingDetails ? (
          <BookingDetailsDrawer
            key={bookingDetailsKey}
            open={bookingDetailsOpen}
            onOpenChange={(open) => {
              setBookingDetailsOpen(open);
              if (!open) bookingDetailsResumeRef.current = null;
            }}
            details={bookingDetails}
            modes={event.bookingFields}
            context="event_checkout"
            onComplete={(saved) => {
              if (bookingDetailsIncomplete(saved, event.bookingFields)) {
                rememberOptionalDetailsSkipped(hiiveSlug, eventSlug);
              }
              const genderWasMissing =
                !bookingDetails.gender && Boolean(saved.gender);
              setBookingDetails(saved);
              setBookingDetailsOpen(false);
              // A balanced ticket shows each person their own side's seats,
              // which the page couldn't know without a gender. Re-read it so
              // a side that's waitlisted shows "Join waitlist", not "Add".
              if (
                genderWasMissing &&
                normalizedOccurrences.some((occurrence) =>
                  occurrence.passes.some((pass) => pass.balance),
                )
              ) {
                router.refresh();
              }
              // Eager, because the resumed submit runs on this same tick and
              // reads the ref. See `deferredAnswersSatisfiedRef`.
              bookingDetailsReadyRef.current = true;
              const resume = bookingDetailsResumeRef.current;
              bookingDetailsResumeRef.current = null;
              resume?.();
            }}
          />
        ) : null}

        <TicketQuestionsDrawer
          key={ticketQuestionsKey}
          open={ticketQuestionsOpen}
          onOpenChange={(open) => {
            setTicketQuestionsOpen(open);
            if (!open) ticketQuestionsResumeRef.current = null;
          }}
          tickets={ticketQuestionGroups}
          initialAnswers={ticketAnswersRef.current}
          onComplete={(answers) => {
            ticketAnswersRef.current = answers;
            for (const group of ticketQuestionGroups) {
              ticketQuestionsAskedRef.current.add(group.passTypeId);
            }
            setTicketQuestionsOpen(false);
            const resume = ticketQuestionsResumeRef.current;
            ticketQuestionsResumeRef.current = null;
            resume?.();
          }}
        />

        <Drawer open={isAttendeeInfoOpen} onOpenChange={setIsAttendeeInfoOpen}>
          <DrawerContent className="mx-auto max-h-[82dvh] max-w-[520px] rounded-t-[30px] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#090b10] dark:text-white">
            <DrawerHeader className="px-4 pb-2 text-left">
              <DrawerTitle className="text-2xl font-semibold text-pretty text-text-primary dark:text-white">
                How attendee assignment works
              </DrawerTitle>
              <DrawerDescription className="text-sm text-text-secondary dark:text-white/60">
                A quick guide to assigning tickets before checkout.
              </DrawerDescription>
            </DrawerHeader>

            <div className="space-y-2 overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
              <div className="overflow-hidden rounded-[24px] border border-border bg-surface-alt dark:border-glass-border-10 dark:bg-glass-bg-04">
                {attendeeInfoItems.map((item, index) => (
                  <div
                    key={item}
                    className={cn(
                      "flex gap-3 px-4 py-4",
                      index > 0 &&
                        "border-t border-border dark:border-glass-border-10",
                    )}
                  >
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface text-[11px] font-semibold text-text-secondary dark:bg-glass-bg-08 dark:text-white/80">
                      {index + 1}
                    </div>
                    <p className="text-sm text-text-secondary dark:text-white/72">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full border-border bg-surface-alt text-text-primary hover:bg-surface-elevated dark:border-glass-border-12 dark:bg-glass-bg-04 dark:text-white dark:hover:bg-glass-bg-08"
                onClick={() => setIsAttendeeInfoOpen(false)}
              >
                Close
              </Button>
            </div>
          </DrawerContent>
        </Drawer>

        {/* Sticky Footer — only visible when tickets are selected */}
        {totalPassesSelected > 0 && (
          <div className="fixed right-0 bottom-0 left-0 border-t border-border bg-background/92 p-4 pb-[max(env(safe-area-inset-bottom),1rem)] backdrop-blur-lg supports-[backdrop-filter]:bg-background/86 lg:hidden">
            <div className="mx-auto flex max-w-4xl items-center justify-between">
              {orderSummaryDetails}

              <div className="flex items-center gap-3">
                {error && (
                  <p className="hidden text-sm text-error sm:block">{error}</p>
                )}
                {checkoutButton}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
